import React from "react";
import ModuleHeaderMenu from "../components/ModuleHeaderMenu";
import { buildNextRecordNumber } from "../lib/recordNumberUtils";
import { appendProvisionalSuffix, appendUpdatePendingDisplaySuffix, markOperationalCreatePending, markOperationalUpdatePending } from "../lib/operationalProvisional";
import { sortAlpha } from "../lib/sortUtils";
export function createHRModule(deps){
  const {
    useState,
    useEffect,
    makeSeed,
    useFarm,
    useFarms,
    useFarmCollection,
    useSysLists,
    useToast,
    useBgSyncToast,
    useReliableSyncOnSave,
    canCreateInModule,
    canEditInModule,
    canDeleteInModule,
    getAccounts,
    syncFarmCollaboratorAccountsToEmployees,
    DB,
    nextId,
    createClientId,
    makeCreateMeta,
    makeUpdateMeta,
    today,
    fmtMoney,
    fmtDate,
    inputStyle,
    setEditingFlag,
    saveAccounts,
    ensureDefaultAdminAccount,
    pushGlobal,
    appendUserDeletionLog,
    isCompactViewport,
    T,
    COST_CENTERS,
    COLLAB_LEVELS,
    FUNC_ROLES,
    FUNC_STATUS,
    EPI_DEFAULTS,
    EQUIP_DEFAULTS,
    ITEM_CONDITIONS,
    DEV_REASONS,
    StatCard,
    Btn,
    Input,
    Field,
    Select,
    Textarea,
    Badge,
    Toast,
    CCBadge,
    PwInput,
    FarmInlineLabel,
  } = deps;

  const normalizeLinkUsername = (value="") => String(value || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9._-]+/g, ".")
    .replace(/\.{2,}/g, ".")
    .replace(/^\.+|\.+$/g, "")
    .slice(0, 40);

  const buildUniqueUsername = (baseValue, existingAccounts = [], reservedIds = [], fallbackYear = "", reservedUsernames = []) => {
    const base = normalizeLinkUsername(baseValue) || "colaborador";
    const taken = new Set([
      ...(Array.isArray(existingAccounts) ? existingAccounts : [])
        .filter(acc => !reservedIds.includes(String(acc?.id || "")))
        .map(acc => String(acc?.username || "").trim().toLowerCase())
        .filter(Boolean),
      ...(Array.isArray(reservedUsernames) ? reservedUsernames : [])
        .map(name => String(name || "").trim().toLowerCase())
        .filter(Boolean),
    ]);
    if (!taken.has(base)) return base;
    const year = String(fallbackYear || "").replace(/\D+/g, "").slice(0, 4);
    if (year) {
      const withYear = `${base}${year}`;
      if (!taken.has(withYear)) return withYear;
      let idx = 2;
      while (taken.has(`${withYear}.${idx}`)) idx += 1;
      return `${withYear}.${idx}`;
    }
    let idx = 2;
    while (taken.has(`${base}.${idx}`)) idx += 1;
    return `${base}.${idx}`;
  };

  const buildEmployeeLoginUsername = (fullName = "", birthDate = "", existingAccounts = [], reservedIds = [], reservedUsernames = []) => {
    const firstName = normalizeLinkUsername(String(fullName || "").trim().split(/\s+/)[0] || "") || "colaborador";
    // A sugestão automática de login usa somente o campo Nome.
    // Sobrenome e data de nascimento são obrigatórios, mas não entram no cálculo do usuário.
    return buildUniqueUsername(firstName, existingAccounts, reservedIds, "", reservedUsernames);
  };

  const buildTemporaryAccessPassword = () => "1234";

  function displayHrRecordNumber(record) {
    if (!record) return "—";
    const rn = String(record.recordNumber || record.id || "").trim();
    if (!rn) return "—";
    if (record.pendingLocalUpdate && !record.pendingLocalCreate) return appendUpdatePendingDisplaySuffix(rn);
    return rn;
  }

  function ensureOperationalRecordNumber(record, records, moduleKey) {
    if (!record || record.recordNumber) return record;
    const nextNumber = buildNextRecordNumber(Array.isArray(records) ? records : [], { moduleKey });
    return { ...record, ...nextNumber, recordNumber: appendProvisionalSuffix(nextNumber.recordNumber) };
  }

  function stableSubClientId(prefix, existingId, extra = "") {
    const raw = String(existingId || "").trim();
    if (raw && !/^\d+$/.test(raw)) return raw;
    return createClientId(`${prefix}${extra ? `_${String(extra).replace(/[^a-z0-9]+/gi, "_").slice(0, 18)}` : ""}`);
  }
  const isValidAccessPassword = (value) => /^.{4,30}$/.test(String(value || "").trim());

  const getUsernameAvailability = (value = "", existingAccounts = [], currentAccountId = "", reservedUsernames = []) => {
    const normalized = normalizeLinkUsername(value);
    if (!normalized) {
      return { normalized: "", available: false, conflict: null, reason: "empty" };
    }
    const conflict = (Array.isArray(existingAccounts) ? existingAccounts : []).find(acc => {
      if (currentAccountId && String(acc?.id || "") === String(currentAccountId)) return false;
      return String(acc?.username || "").trim().toLowerCase() === normalized;
    }) || null;
    const reservedConflict = !conflict && (Array.isArray(reservedUsernames) ? reservedUsernames : []).some(name => String(name || "").trim().toLowerCase() === normalized);
    return {
      normalized,
      available: !conflict && !reservedConflict,
      conflict,
      reason: conflict ? "taken" : reservedConflict ? "historical" : "available",
    };
  };

  const splitEmployeeNameParts = (value="") => {
    const full = String(value || "").trim().replace(/\s+/g, " ");
    if (!full) return { firstName:"", lastName:"" };
    const parts = full.split(" ").filter(Boolean);
    return { firstName: parts[0] || "", lastName: parts.slice(1).join(" ") };
  };

  const autoFillEmployeeAccess = (draft) => {
    const safe = draft && typeof draft === "object" ? { ...draft } : {};
    const firstNameRaw = String(safe.firstName || "");
    const lastNameRaw = String(safe.lastName || "");
    const firstName = firstNameRaw.replace(/^\s+/, "");
    const lastName = lastNameRaw.replace(/^\s+/, "");
    const composedName = [firstName, lastName]
      .map(v => String(v || "").trim())
      .filter(Boolean)
      .join(" ")
      .trim();
    safe.firstName = firstName;
    safe.lastName = lastName;
    safe.name = composedName;
    const reservedAccountIds = safe.linkedAccountId ? [String(safe.linkedAccountId)] : [];
    const shouldAutoFillUsername = safe._new !== false && safe.usernameAuto !== false;
    if (shouldAutoFillUsername) {
      const existingAccounts = Array.isArray(safe.existingAccounts) ? safe.existingAccounts : [];
      const reservedUsernames = Array.isArray(safe.reservedUsernames) ? safe.reservedUsernames : [];
      safe.username = composedName
        ? buildEmployeeLoginUsername(composedName, safe.birthDate || "", existingAccounts, reservedAccountIds, reservedUsernames)
        : "";
    }
    if (safe._new !== false) {
      safe.usernameAuto = shouldAutoFillUsername;
      safe.password = buildTemporaryAccessPassword();
    } else if (!String(safe.password || "").trim()) {
      safe.password = "";
    }
    return safe;
  };

function EmployeeChecklist({ empId, empName, checklists, setChecklists }) {
  const SL = useSysLists();
  const [itemForm,   setItemForm]  = useState(null);
  const [confForm,   setConfForm]  = useState(null);  // null | { _new, date, items:[{itemId,itemName,condition,obs}] }
  const [toast, show]              = useToast();

  const myList = checklists.find(c => c.empId === empId) || { empId, items:[], conferences:[] };

  function persist(updated) {
    const now = new Date().toISOString();
    setChecklists(p => {
      const list = Array.isArray(p) ? p : [];
      const exists = list.find(c => c.empId === empId);
      const normalized = {
        ...updated,
        id: updated?.id || updated?.clientId || stableSubClientId("chk", empId),
        clientId: updated?.clientId || updated?.id || stableSubClientId("chk", empId),
        empId,
        empName,
        updatedAt: now,
      };
      const pending = exists
        ? markOperationalUpdatePending(normalized, { userName: empName || "Sistema", at: now })
        : markOperationalCreatePending(ensureOperationalRecordNumber(normalized, list, "checklists"), { userName: empName || "Sistema", at: now });
      return exists ? list.map(c => c.empId === empId ? pending : c) : [...list, pending];
    });
  }

  function toggleItem(itemId) {
    persist({ ...myList, items: myList.items.map(i =>
      i.id === itemId ? { ...i, clientId: i.clientId || i.id || stableSubClientId("epi", itemId), withEmployee: !i.withEmployee, updatedAt: new Date().toISOString() } : i
    )});
  }

  function saveItem() {
    if (!itemForm.name.trim()) { show("Nome obrigatório.", "error"); return; }
    const { _new, ...d } = itemForm;
    const now = new Date().toISOString();
    const updatedItems = _new
      ? [...myList.items, { ...d, id: stableSubClientId("epi", nextId(myList.items)), clientId: stableSubClientId("epi", nextId(myList.items)), withEmployee: true, createdAt: now, updatedAt: now }]
      : myList.items.map(i => i.id === d.id ? { ...i, ...d, clientId: d.clientId || i.clientId || i.id || stableSubClientId("epi", d.id), updatedAt: now } : i);
    persist({ ...myList, items: updatedItems });
    show("✅ Salvo!"); setItemForm(null);
  }

  function removeItem(id) {
    persist({ ...myList, items: myList.items.map(i => i.id === id ? { ...i, _deleted:true, deletedAt:new Date().toISOString(), withEmployee:false, updatedAt:new Date().toISOString() } : i) });
    show("🗑️ Removido."); setItemForm(null);
  }

  // Open a new conference form pre-filled with all current items
  function openNewConference() {
    const confItems = myList.items.filter(i=>!i?._deleted && !i?.deletedAt).map(i => ({
      id: stableSubClientId("confitem", i.clientId || i.id),
      clientId: stableSubClientId("confitem", i.clientId || i.id),
      itemId: i.id, itemClientId: i.clientId || i.id, itemName: i.name, itemType: i.type,
      condition: "Conformidade", obs: ""
    }));
    setConfForm({ _new: true, date: today(), items: confItems });
  }

  function saveConference() {
    if (!confForm.date) { show("Selecione uma data.", "error"); return; }
    const { _new, ...d } = confForm;
    const now = new Date().toISOString();
    const confId = d.id || stableSubClientId("conf", nextId(myList.conferences));
    const normalizedConf = { ...d, id: confId, clientId: d.clientId || confId, updatedAt: now, items: (d.items||[]).map((item, idx)=>({ ...item, id:item.id||item.clientId||stableSubClientId("confitem", `${confId}_${idx}`), clientId:item.clientId||item.id||stableSubClientId("confitem", `${confId}_${idx}`) })) };
    const updatedConfs = _new
      ? [...myList.conferences, { ...normalizedConf, createdAt: now }]
      : myList.conferences.map(c => c.id === d.id ? { ...c, ...normalizedConf } : c);
    persist({ ...myList, conferences: updatedConfs });
    show("📅 Conferência salva!"); setConfForm(null);
  }

  function removeConference(id) {
    persist({ ...myList, conferences: myList.conferences.map(c => c.id === id ? { ...c, _deleted:true, deletedAt:new Date().toISOString(), updatedAt:new Date().toISOString() } : c) });
  }

  function addPreset(name, type) {
    if (myList.items.find(i => i.name.toLowerCase() === name.toLowerCase())) { show("Item já existe.", "error"); return; }
    const now = new Date().toISOString();
    const nextItemId = stableSubClientId("epi", `${type}_${name}`);
    persist({ ...myList, items: [...myList.items, { id: nextItemId, clientId: nextItemId, name, type, withEmployee: true, deliveredAt: today(), returnedAt: "", returnReason: "", notes: "", createdAt: now, updatedAt: now }] });
    show(`✅ ${name} adicionado!`);
  }

  // Update a single item's condition inside the conf form
  function setConfItemField(itemId, field, value) {
    setConfForm(f => ({ ...f, items: f.items.map(ci => ci.itemId === itemId ? { ...ci, [field]: value } : ci) }));
  }

  const epis   = myList.items.filter(i => i.type === "EPI");
  const equips = myList.items.filter(i => i.type === "Equipamento");
  const defItem = (type) => ({ _new:true, name:"", type, withEmployee:true, deliveredAt:today(), returnedAt:"", returnReason:DEV_REASONS[0], notes:"" });

  const condMeta = (val) => ITEM_CONDITIONS.find(c => c.value === val) || ITEM_CONDITIONS[0];

  const ItemSection = ({ title, icon, color, items, type }) => (
    <div style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:12, padding:20, marginBottom:16 }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          <span style={{ fontSize:20 }}>{icon}</span>
          <span style={{ fontFamily:"'Playfair Display',serif", fontSize:16, fontWeight:700, color:T.text }}>{title}</span>
          <Badge color={color}>{items.filter(i=>i.withEmployee).length}/{items.length}</Badge>
        </div>
        <Btn size="sm" onClick={() => setItemForm(defItem(type))}>+ Adicionar</Btn>
      </div>
      <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:14 }}>
        {(type==="EPI"?EPI_DEFAULTS:EQUIP_DEFAULTS).map(n => (
          <button key={n} onClick={() => addPreset(n, type)}
            style={{ background:"transparent", border:`1px dashed ${T.border}`, borderRadius:8, padding:"4px 10px", cursor:"pointer", fontSize:11, color:T.muted, transition:"all .15s" }}
            onMouseEnter={e=>{e.currentTarget.style.borderColor=color;e.currentTarget.style.color=color;}}
            onMouseLeave={e=>{e.currentTarget.style.borderColor=T.border;e.currentTarget.style.color=T.muted;}}>
            + {n}
          </button>
        ))}
      </div>
      {items.length === 0 && <div style={{ color:T.muted, fontSize:13, padding:"8px 0" }}>Nenhum item cadastrado ainda.</div>}
      {items.map(item => (
        <div key={item.id} style={{ display:"flex", alignItems:"center", gap:12, padding:"10px 0", borderBottom:`1px solid ${T.border}22` }}>
          <div onClick={() => toggleItem(item.id)} style={{ width:22, height:22, borderRadius:6, border:`2px solid ${item.withEmployee?color:T.border}`, background:item.withEmployee?`${color}22`:"transparent", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, transition:"all .15s" }}>
            {item.withEmployee && <span style={{ color, fontSize:13, fontWeight:700 }}>✓</span>}
          </div>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontWeight:600, color:item.withEmployee?T.text:T.muted, fontSize:14, textDecoration:item.withEmployee?"none":"line-through" }}>{item.name}</div>
            <div style={{ fontSize:11, color:T.muted, marginTop:2, display:"flex", gap:10, flexWrap:"wrap" }}>
              {item.deliveredAt && <span>📦 Entregue: {fmtDate(item.deliveredAt)}</span>}
              {item.returnedAt  && <span style={{color:T.yellow}}>↩ Devolvido: {fmtDate(item.returnedAt)} · {item.returnReason}</span>}
              {item.notes       && <span>📝 {item.notes}</span>}
            </div>
          </div>
          <Badge color={item.withEmployee?color:T.muted}>{item.withEmployee?"Com colaborador(a)":"Devolvido"}</Badge>
          <button onClick={() => setItemForm({...item, _new:false})} style={{ background:"none", border:`1px solid ${T.border}`, borderRadius:7, padding:"5px 9px", cursor:"pointer", color:T.muted, fontSize:11 }}>✏️</button>
        </div>
      ))}
    </div>
  );

  // Conference detail view (read-only or edit)
  const ConferenceDetail = ({ conf }) => {
    const editing = confForm && !confForm._new && confForm.id === conf.id;
    const data = editing ? confForm : conf;
    return (
      <div style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:12, padding:20, marginBottom:16 }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            <span style={{ fontSize:20 }}>📋</span>
            <div>
              <div style={{ fontFamily:"'Playfair Display',serif", fontSize:16, fontWeight:700, color:T.text }}>Conferência — {fmtDate(data.date)}</div>
              <div style={{ fontSize:12, color:T.muted }}>{(data.items||[]).length} itens avaliados</div>
            </div>
          </div>
          <div style={{ display:"flex", gap:8 }}>
            {!editing && <Btn size="sm" variant="ghost" onClick={() => { setConfForm({...conf, _new:false}); }}>✏️ Editar</Btn>}
            {editing  && <Btn size="sm" onClick={saveConference}>💾 Salvar</Btn>}
            {editing  && <Btn size="sm" variant="ghost" onClick={() => setConfForm(null)}>Cancelar</Btn>}
            <Btn size="sm" variant="danger" onClick={() => removeConference(conf.id)}>🗑️</Btn>
          </div>
        </div>
        {editing && <div style={{ marginBottom:14 }}>
          <Field label="Data da Conferência"><Input type="date" value={confForm.date} onChange={e=>setConfForm(f=>({...f,date:e.target.value}))} style={{width:200}}/></Field>
        </div>}
        {(data.items||[]).length === 0 && <div style={{ color:T.muted, fontSize:13 }}>Nenhum item registrado nesta conferência.</div>}
        {(data.items||[]).map(ci => {
          const cm = condMeta(ci.condition);
          return (
            <div key={ci.itemId} style={{ display:"flex", alignItems:"center", gap:12, padding:"10px 0", borderBottom:`1px solid ${T.border}22`, flexWrap:"wrap" }}>
              <div style={{ minWidth:140, flex:"0 0 140px" }}>
                <div style={{ fontWeight:600, color:T.text, fontSize:13 }}>{ci.itemName}</div>
                <div style={{ fontSize:11, color:T.muted }}>{ci.itemType}</div>
              </div>
              {editing ? (
                <div style={{ flex:1, display:"flex", gap:8, flexWrap:"wrap", alignItems:"center" }}>
                  <div style={{ display:"flex", gap:5, flexWrap:"wrap" }}>
                    {ITEM_CONDITIONS.map(cond => (
                      <button key={cond.value} onClick={() => setConfItemField(ci.itemId, "condition", cond.value)}
                        style={{ padding:"4px 10px", borderRadius:8, fontSize:11, fontWeight:600, cursor:"pointer", border:`1.5px solid ${ci.condition===cond.value?cond.color:T.border}`, background:ci.condition===cond.value?`${cond.color}22`:"transparent", color:ci.condition===cond.value?cond.color:T.muted, transition:"all .12s" }}>
                        {cond.icon} {cond.label}
                      </button>
                    ))}
                  </div>
                  <input value={ci.obs} onChange={e=>setConfItemField(ci.itemId,"obs",e.target.value)} placeholder="Observação..." style={{...inputStyle,flex:1,minWidth:160,fontSize:12}}/>
                </div>
              ) : (
                <div style={{ flex:1, display:"flex", alignItems:"center", gap:10, flexWrap:"wrap" }}>
                  <span style={{ background:`${cm.color}22`, color:cm.color, border:`1px solid ${cm.color}44`, borderRadius:8, padding:"3px 10px", fontSize:11, fontWeight:700 }}>{cm.icon} {cm.label}</span>
                  {ci.obs && <span style={{ color:T.muted, fontSize:12 }}>📝 {ci.obs}</span>}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div>
      <Toast toast={toast} />
      <ItemSection title="EPIs" icon="🦺" color={T.green} items={epis} type="EPI" />
      <ItemSection title="Equipamentos" icon="📻" color={T.accent} items={equips} type="Equipamento" />

      {/* Conferences */}
      <div style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:12, padding:20 }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
          <div style={{ fontFamily:"'Playfair Display',serif", fontSize:16, fontWeight:700, color:T.text }}>📅 Conferências</div>
          <Btn onClick={openNewConference} disabled={myList.items.length===0}>+ Nova Conferência</Btn>
        </div>
        {myList.items.length===0 && <div style={{ color:T.muted, fontSize:13 }}>Cadastre EPIs ou equipamentos antes de criar uma conferência.</div>}

        {/* New conference form */}
        {confForm && confForm._new && (
          <div style={{ background:T.card2, border:`1px solid ${T.accent}44`, borderRadius:12, padding:20, marginBottom:16 }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
              <div style={{ fontFamily:"'Playfair Display',serif", fontSize:15, fontWeight:700, color:T.text }}>📋 Nova Conferência</div>
              <div style={{ display:"flex", gap:8 }}>
                <Btn size="sm" onClick={saveConference}>💾 Salvar</Btn>
                <Btn size="sm" variant="ghost" onClick={() => setConfForm(null)}>Cancelar</Btn>
              </div>
            </div>
            <div style={{ marginBottom:16 }}>
              <Field label="Data da Conferência"><Input type="date" value={confForm.date} onChange={e=>setConfForm(f=>({...f,date:e.target.value}))} style={{width:200}}/></Field>
            </div>
            {confForm.items.length === 0 && <div style={{ color:T.muted, fontSize:13 }}>Nenhum item para avaliar.</div>}
            {confForm.items.map(ci => (
              <div key={ci.itemId} style={{ display:"flex", alignItems:"flex-start", gap:12, padding:"12px 0", borderBottom:`1px solid ${T.border}22`, flexWrap:"wrap" }}>
                <div style={{ minWidth:140, flex:"0 0 140px", paddingTop:4 }}>
                  <div style={{ fontWeight:600, color:T.text, fontSize:13 }}>{ci.itemName}</div>
                  <div style={{ fontSize:11, color:T.muted }}>{ci.itemType}</div>
                </div>
                <div style={{ flex:1, display:"flex", gap:8, flexWrap:"wrap", alignItems:"center" }}>
                  <div style={{ display:"flex", gap:5, flexWrap:"wrap" }}>
                    {ITEM_CONDITIONS.map(cond => (
                      <button key={cond.value} onClick={() => setConfItemField(ci.itemId, "condition", cond.value)}
                        style={{ padding:"5px 11px", borderRadius:8, fontSize:11, fontWeight:600, cursor:"pointer", border:`1.5px solid ${ci.condition===cond.value?cond.color:T.border}`, background:ci.condition===cond.value?`${cond.color}22`:"transparent", color:ci.condition===cond.value?cond.color:T.muted, transition:"all .12s" }}>
                        {cond.icon} {cond.label}
                      </button>
                    ))}
                  </div>
                  <input value={ci.obs} onChange={e=>setConfItemField(ci.itemId,"obs",e.target.value)} placeholder="Observação opcional..." style={{...inputStyle,flex:1,minWidth:160,fontSize:12}}/>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Existing conferences */}
        {[...myList.conferences].sort((a,b)=>b.date.localeCompare(a.date)).map(conf => (
          <ConferenceDetail key={conf.id} conf={conf} />
        ))}
        {myList.conferences.length === 0 && myList.items.length > 0 && <div style={{ color:T.muted, fontSize:13 }}>Nenhuma conferência registrada ainda.</div>}
      </div>
      {/* Item form inline */}
      {itemForm&&<div style={{marginTop:18,background:T.card,border:`1px solid ${T.border}`,borderRadius:18,padding:18,boxShadow:`0 10px 24px ${T.shadow || "rgba(0,0,0,.08)"}`}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:isCompactViewport()?"flex-start":"center",gap:12,flexWrap:"wrap",marginBottom:14}}>
          <div>
            <div style={{fontSize:11,fontWeight:800,color:T.accent,textTransform:"uppercase",letterSpacing:.8}}>Fluxo inline</div>
            <div style={{fontSize:18,fontWeight:800,color:T.text,marginTop:4}}>{itemForm._new?`Novo ${itemForm.type}`:`Editar ${itemForm.type}`}</div>
          </div>
          <Btn variant="ghost" onClick={()=>setItemForm(null)}>Fechar editor</Btn>
        </div>
        {itemForm&&<div style={{display:"flex",flexDirection:"column",gap:14}}>
          <Field label="Nome *"><Input value={itemForm.name} onChange={e=>setItemForm(f=>({...f,name:e.target.value}))} placeholder={itemForm.type==="EPI"?"Ex: Luva, Bota...":"Ex: Rádio, Furadeira..."}/></Field>
          <Field label="Tipo"><Select value={itemForm.type} onChange={e=>setItemForm(f=>({...f,type:e.target.value}))}><option value="EPI">EPI</option><option value="Equipamento">Equipamento</option></Select></Field>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            <Field label="Data de Entrega"><Input type="date" value={itemForm.deliveredAt} onChange={e=>setItemForm(f=>({...f,deliveredAt:e.target.value}))}/></Field>
            <Field label="Data de Devolução"><Input type="date" value={itemForm.returnedAt} onChange={e=>setItemForm(f=>({...f,returnedAt:e.target.value,withEmployee:!e.target.value}))}/></Field>
          </div>
          {itemForm.returnedAt&&<Field label="Motivo da Devolução"><Select value={itemForm.returnReason} onChange={e=>setItemForm(f=>({...f,returnReason:e.target.value}))}>{(SL.DEV_REASONS||DEV_REASONS).map(r=><option key={r}>{r}</option>)}</Select></Field>}
          <Field label="Status">
            <div style={{display:"flex",gap:10,marginTop:4}}>
              {[true,false].map(v=>(
                <div key={String(v)} onClick={()=>setItemForm(f=>({...f,withEmployee:v}))}
                  style={{flex:1,padding:"10px 0",textAlign:"center",borderRadius:8,cursor:"pointer",border:`2px solid ${itemForm.withEmployee===v?(v?T.green:T.yellow):T.border}`,background:itemForm.withEmployee===v?(v?`${T.green}18`:`${T.yellow}18`):"transparent",color:itemForm.withEmployee===v?(v?T.green:T.yellow):T.muted,fontWeight:600,fontSize:13}}>
                  {v?"✅ Com Colaborador(a)":"↩ Devolvido"}
                </div>
              ))}
            </div>
          </Field>
          <Field label="Observações"><Textarea rows={2} value={itemForm.notes} onChange={e=>setItemForm(f=>({...f,notes:e.target.value}))}/></Field>
          <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
            {!itemForm._new&&<Btn variant="danger" onClick={()=>removeItem(itemForm.id)}>🗑️ Excluir</Btn>}
            <Btn variant="ghost" onClick={()=>setItemForm(null)}>Cancelar</Btn>
            <Btn onClick={saveItem}>{itemForm._new?"Adicionar":"Salvar"}</Btn>
          </div>
        </div>}
      </div>}
    </div>
  );
}

function dedupeEmployeesForHR(list, currentFarmId){
  const normalize = (v) => String(v || "").trim().toLowerCase();
  const normalizeDigits = (v) => String(v || "").replace(/\D+/g, "");
  const buildKeys = (emp) => {
    const keys = [];
    const id = String(emp?.id || "").trim();
    const linkedAccountId = String(emp?.linkedAccountId || "").trim();
    const userId = String(emp?.userId || "").trim();
    const username = normalize(emp?.username);
    const cpf = normalizeDigits(emp?.cpf);
    const email = normalize(emp?.email);
    const phone = normalizeDigits(emp?.phone);
    const name = normalize(emp?.name);
    const role = normalize(emp?.role);
    const hired = String(emp?.hired || "").trim();
    const farmId = String(emp?.primaryFarmId || emp?.farmId || currentFarmId || "").trim();
    if (id) keys.push(`id:${id}`);
    if (linkedAccountId) keys.push(`acc:${linkedAccountId}`);
    if (userId) keys.push(`user:${userId}`);
    if (username) keys.push(`usr:${username}`);
    if (cpf) keys.push(`cpf:${cpf}`);
    if (email) keys.push(`mail:${email}`);
    if (phone) keys.push(`phone:${phone}`);
    if (name && farmId) keys.push(`namefarm:${farmId}:${name}`);
    if (name && role && farmId) keys.push(`namefarmrole:${farmId}:${name}:${role}`);
    if (name && hired && farmId) keys.push(`namefarmhired:${farmId}:${name}:${hired}`);
    return [...new Set(keys)];
  };
  const chooseBetter = (a, b) => {
    const score = (emp) => {
      let s = 0;
      if (emp?.id) s += 20;
      if (emp?.linkedAccountId) s += 10;
      if (emp?.userId) s += 8;
      if (emp?.username) s += 6;
      if (emp?.cpf) s += 4;
      if (emp?.email) s += 2;
      if (emp?.phone) s += 1;
      if (emp?.name) s += 2;
      if (emp?.hired) s += 1;
      if (emp?.updatedAt) s += 1;
      return s;
    };
    const tsValue = (emp) => String(emp?.deactivatedAt || emp?.deactivated_at || emp?.updatedAt || emp?.createdAt || "").trim();
    const aTs = tsValue(a);
    const bTs = tsValue(b);
    if (aTs && bTs && aTs !== bTs) {
      const latest = bTs > aTs ? b : a;
      const older = latest === b ? a : b;
      const mergedLatest = { ...older, ...latest };
      if (String(latest?.status || "") === "Inativo" || latest?.active === false) {
        mergedLatest.status = "Inativo";
        mergedLatest.active = false;
        mergedLatest.deactivatedAt = mergedLatest.deactivatedAt || latest?.deactivatedAt || latest?.deactivated_at || older?.deactivatedAt || older?.deactivated_at || "";
        mergedLatest.deactivated_at = mergedLatest.deactivated_at || mergedLatest.deactivatedAt || "";
      }
      return {
        ...mergedLatest,
        id: String(mergedLatest?.id || a?.id || b?.id || createClientId("emp")),
        farmId: mergedLatest?.farmId || currentFarmId || "",
        primaryFarmId: mergedLatest?.primaryFarmId || mergedLatest?.farmId || currentFarmId || "",
        sharedFarms: Array.isArray(mergedLatest?.sharedFarms) ? mergedLatest.sharedFarms : [],
        warnings: Array.isArray(mergedLatest?.warnings) ? mergedLatest.warnings : [],
        gestorVisibleFields: Array.isArray(mergedLatest?.gestorVisibleFields) ? mergedLatest.gestorVisibleFields : ["hired"],
      };
    }
    const aScore = score(a);
    const bScore = score(b);
    const base = bScore > aScore ? { ...a, ...b } : { ...b, ...a };
    if (a?.status === "Inativo" || b?.status === "Inativo" || a?.active === false || b?.active === false) {
      const aDeact = !!(a?.status === "Inativo" || a?.active === false);
      const bDeact = !!(b?.status === "Inativo" || b?.active === false);
      if (aDeact && !bDeact) {
        Object.assign(base, { ...b, ...a });
      } else if (bDeact && !aDeact) {
        Object.assign(base, { ...a, ...b });
      }
      base.status = "Inativo";
      base.active = false;
      base.deactivatedAt = base.deactivatedAt || a?.deactivatedAt || a?.deactivated_at || b?.deactivatedAt || b?.deactivated_at || "";
      base.deactivated_at = base.deactivated_at || base.deactivatedAt || "";
    }
    return {
      ...base,
      id: String(base?.id || a?.id || b?.id || createClientId("emp")),
      farmId: base?.farmId || currentFarmId || "",
      primaryFarmId: base?.primaryFarmId || base?.farmId || currentFarmId || "",
      sharedFarms: Array.isArray(base?.sharedFarms) ? base.sharedFarms : [],
      warnings: Array.isArray(base?.warnings) ? base.warnings : [],
      gestorVisibleFields: Array.isArray(base?.gestorVisibleFields) ? base.gestorVisibleFields : ["hired"],
    };
  };

  const out = [];
  const keyToIndex = new Map();
  for (const item of Array.isArray(list) ? list : []) {
    if (item?._deleted || item?.deletedAt || item?.deleted_at) continue;
    const emp = {
      ...(item || {}),
      id: String(item?.id || createClientId("emp")),
      farmId: item?.farmId || currentFarmId || "",
      primaryFarmId: item?.primaryFarmId || item?.farmId || currentFarmId || "",
      sharedFarms: Array.isArray(item?.sharedFarms) ? item.sharedFarms : [],
      warnings: Array.isArray(item?.warnings) ? item.warnings : [],
      gestorVisibleFields: Array.isArray(item?.gestorVisibleFields) ? item.gestorVisibleFields : ["hired"],
    };
    const hits = [...new Set(buildKeys(emp).map(k => keyToIndex.get(k)).filter(i => i !== undefined))];
    if (!hits.length) {
      const idx = out.push(emp) - 1;
      for (const key of buildKeys(emp)) keyToIndex.set(key, idx);
      continue;
    }
    const targetIdx = hits[0];
    let merged = chooseBetter(out[targetIdx], emp);
    for (let i = 1; i < hits.length; i++) {
      const dupIdx = hits[i];
      if (dupIdx === targetIdx || !out[dupIdx]) continue;
      merged = chooseBetter(merged, out[dupIdx]);
      out[dupIdx] = null;
    }
    out[targetIdx] = merged;
    for (const key of buildKeys(merged)) keyToIndex.set(key, targetIdx);
  }
  return out.filter(Boolean);
}

function HRModule({isGestor, session}){
  const seed=makeSeed();
  const SL = useSysLists();
  const[employees,setEmployees]=useFarmCollection("employees",seed.employees);
  const[checklists,setChecklists]=useFarmCollection("checklists",[]);
  const ensureEmployeeRecordNumbers = React.useCallback((records=[])=>{
    const working = Array.isArray(records) ? records.map(item=> item && typeof item === "object" ? { ...item } : item).filter(Boolean) : [];
    working.forEach((record)=>{
      if (!record || record.recordNumber) return;
      Object.assign(record, buildNextRecordNumber(working.filter(Boolean), { moduleKey:"employees" }));
    });
    return working;
  }, []);
  const[services] = useFarmCollection("services", []);
  const[occurrences] = useFarmCollection("occurrences", []);
  const[orders] = useFarmCollection("orders", []);
  const[inspections] = useFarmCollection("inspections", []);
  const[accounts,setAccounts]=useState([]);
  const moduleCardsConfig = (()=>{
    try{
      const parsed = JSON.parse(localStorage.getItem("fz:moduleCardsConfig") || "null");
      return parsed && typeof parsed === "object" ? parsed : {};
    }catch(_){ return {}; }
  })();
  const getConfiguredSectionCards = React.useCallback((sectionId, fallback=[])=>{
    const moduleConfig = moduleCardsConfig?.hr;
    if(moduleConfig && Object.prototype.hasOwnProperty.call(moduleConfig, sectionId)){
      const configured = moduleConfig?.[sectionId];
      if(Array.isArray(configured)) return configured.map(v=>String(v||""));
      return [];
    }
    return (Array.isArray(fallback) ? fallback : []).map(v=>String(v||""));
  }, [moduleCardsConfig]);
  const allowedRootCards = React.useMemo(()=>{
    const direct = getConfiguredSectionCards("moduleHome", []);
    return Array.isArray(direct) && direct.length ? direct : ["numero_colaboradores","equipamentos_entregues","epis_entregues","conferencia_epis","conferencia_equipamentos"];
  }, [getConfiguredSectionCards]);
  const allowedEmployeesMenuCards = React.useMemo(()=>{
    const direct = getConfiguredSectionCards("menu:colaboradores", []);
    return Array.isArray(direct) && direct.length ? direct : allowedRootCards;
  }, [allowedRootCards, getConfiguredSectionCards]);
  const allowedItemsMenuCards = React.useMemo(()=>{
    const direct = getConfiguredSectionCards("menu:epis_equipamentos", []);
    return Array.isArray(direct) && direct.length ? direct : ["equipamentos_entregues","epis_entregues","conferencia_epis","conferencia_equipamentos"];
  }, [getConfiguredSectionCards]);
  const { fid: currentFarmId } = useFarm();
  const[allFarms]=useFarms();
  const activeFarms = (Array.isArray(allFarms) ? allFarms : []).filter(f => !f?._deleted && !f?.deletedAt && !f?.deleted_at && !f?.isArchived && !f?.archivedAt);

  // Load accounts on mount using proper DB layer
  useEffect(()=>{
    let cancelled = false;
    const reloadAccounts = async ()=>{
      try{
        const nextAccounts = await getAccounts();
        if(!cancelled) setAccounts(Array.isArray(nextAccounts) ? nextAccounts : []);
      }catch(_){
        if(!cancelled) setAccounts([]);
      }
    };
    const onSyncUpdated = (e)=>{
      if(!e?.detail?.key || e.detail.key === "auth:accounts") reloadAccounts();
    };
    reloadAccounts();
    window.addEventListener("fz:sync:updated", onSyncUpdated);
    return ()=>{
      cancelled = true;
      window.removeEventListener("fz:sync:updated", onSyncUpdated);
    };
  },[]);
  const [userDeletionLog, setUserDeletionLog] = useState([]);
  useEffect(()=>{
    let cancelled = false;
    const reloadDeletionLog = async ()=>{
      try {
        const nextLog = await DB.get("fz:userDeletionLog");
        if(!cancelled) setUserDeletionLog(Array.isArray(nextLog) ? nextLog : []);
      } catch(_) {
        if(!cancelled) setUserDeletionLog([]);
      }
    };
    const onSyncUpdated = (e)=>{
      if(!e?.detail?.key || e.detail.key === "fz:userDeletionLog") reloadDeletionLog();
    };
    reloadDeletionLog();
    window.addEventListener("fz:sync:updated", onSyncUpdated);
    return ()=>{
      cancelled = true;
      window.removeEventListener("fz:sync:updated", onSyncUpdated);
    };
  },[]);
  const reservedUsernames = React.useMemo(()=>[
    ...new Set((Array.isArray(userDeletionLog) ? userDeletionLog : [])
      .map(item => String(item?.username || "").trim().toLowerCase())
      .filter(Boolean))
  ], [userDeletionLog]);
  const actorName = session?.name || "sistema";
  const employeesView = dedupeEmployeesForHR(employees, currentFarmId);

  useEffect(()=>{
    if(!currentFarmId) return;
    let cancelled = false;
    syncFarmCollaboratorAccountsToEmployees(currentFarmId, actorName).then(()=>{
      if(cancelled) return;
    }).catch(()=>{});
    return ()=>{ cancelled = true; };
  },[actorName, currentFarmId]);
  const[form,setForm]=useState(null);
  const[deleteFlow,setDeleteFlow]=useState(null);
  const[reactivateFlow,setReactivateFlow]=useState(null);
  const[tab,setTab]=useState("emp");
  const[checkEmp,setCheckEmp]=useState(null);
  const[employeeStatusFilter,setEmployeeStatusFilter]=useState("Ativos");
  const[employeeHistory,setEmployeeHistory]=useState(null);

  useEffect(()=>{
    const normalized = ensureEmployeeRecordNumbers(employees||[]);
    if ((normalized||[]).some((item, idx)=>String(item?.recordNumber||"") !== String((employees||[])[idx]?.recordNumber||""))) {
      setEmployees(normalized);
    }
  }, [employees, ensureEmployeeRecordNumbers, setEmployees]);
  const hrInlineTopRef = React.useRef(null);
  const hrInlineRefs = React.useRef(new Map());
  const usernameAvailability = React.useMemo(()=>{
    const adminMode = session?.role === "admin";
    if(!form || !adminMode) return null;
    const currentAccountId = form?.linkedAccountId || "";
    return getUsernameAvailability(form?.username || "", accounts, currentAccountId, reservedUsernames);
  },[accounts, form, reservedUsernames, session?.role]);

  const filteredEmployeesView = (()=>{
    const list = sortAlpha(Array.isArray(employeesView) ? employeesView : [], item => item?.name || item?.firstName || item?.username || "");
    const isInactive = (e) => e?.active === false || String(e?.status || "").trim().toLowerCase() === "inativo" || !!(e?.deactivatedAt || e?.deactivated_at);
    if(employeeStatusFilter === "Ativos") return list.filter(e => !isInactive(e));
    if(employeeStatusFilter === "Desativados") return list.filter(isInactive);
    return list;
  })();
  const formScrollKey = form ? `${form._new ? "new" : "edit"}:${form?.id || ""}` : "";
  React.useEffect(()=>{
    if(!formScrollKey) return;
    const [mode, id] = String(formScrollKey || "").split(":");
    const target = mode === "new" ? hrInlineTopRef.current : hrInlineRefs.current.get(String(id || "")) || hrInlineTopRef.current;
    if(target && typeof target.scrollIntoView === "function") setTimeout(()=>target.scrollIntoView({behavior:"smooth", block:"center"}), 40);
  }, [formScrollKey]);

  const composeEmployeeName = (firstNameValue="", lastNameValue="") => [
    String(firstNameValue || "").trim(),
    String(lastNameValue || "").trim(),
  ].filter(Boolean).join(" ").trim();

  const updateEmployeeNameField = (field, value) => {
    setForm(f=>{
      const next = { ...(f || {}), [field]: String(value || "").replace(/^\s+/, "") };
      next.name = composeEmployeeName(next.firstName, next.lastName);
      return next;
    });
  };

  const refreshEmployeeAccessSuggestion = () => {
    setForm(f=>{
      if(!f || f._new === false || f.usernameAuto === false) return f;
      return autoFillEmployeeAccess({
        ...f,
        usernameAuto:true,
        existingAccounts:accounts,
        reservedUsernames,
      });
    });
  };

  function openEmployeeEditor(record){
    if(!record) return;
    const parts = splitEmployeeNameParts(record?.name || "");
    setEditingFlag(true);
    setForm(autoFillEmployeeAccess({
      ...record,
      _new:false,
      usernameAuto:false,
      existingAccounts:accounts,
      reservedUsernames,
      firstName: record?.firstName || parts.firstName,
      lastName: record?.lastName || parts.lastName,
      primaryFarmId: record.primaryFarmId || currentFarmId || "",
      sharedFarms: record.sharedFarms || [],
    }));
  }

  const[toast,show]=useToast();
  useBgSyncToast("employees", show);
  const isAdmin = session?.role==="admin";
  const canCreate = canCreateInModule(session,"hr") || canEditInModule(session,"hr");
  const canEdit = canEditInModule(session,"hr");
  const canDelete = canDeleteInModule(session,"hr");

  // Gestor visible fields are stored in employee.gestorVisibleFields

  // Salary cost for THIS farm = primary employees 100% + shared employees by %
  // (kept documented here; current screen does not use this aggregate directly)

  const defForm={
    _new:true, name:"", firstName:"", lastName:"", role:FUNC_ROLES[0], costCenter:COST_CENTERS[0],
    status:"Ativo", phone:"", cpf:"", rg:"", birthDate:"", email:"", pix:"",
    // Admin-only fields
    salary:0, hired:today(), notes:"", warnings:[],
    // Access control
    username:"", usernameAuto:true, password:"",
    gestorVisibleFields:["hired"],
    collabLevel: 1,  // 1=Operacional, 2=Técnico, 3=Supervisor
    // Multi-farm allocation
    primaryFarmId: currentFarmId || "",
    sharedFarms: [],
  };

  const syncOnSave = useReliableSyncOnSave();

  async function persistEmployeesAndConfirm(farmId, nextEmployees, fallbackToQueuedSync = true){
    const empKey = `fz:${farmId}:employees`;
    await DB.set(empKey, nextEmployees);
    try {
      window.dispatchEvent(new CustomEvent("fz:sync:updated", { detail: { key: empKey, source: "local-write" } }));
    } catch {}
    if (fallbackToQueuedSync) syncOnSave(farmId, "employees");
    return nextEmployees;
  }

  async function deactivateLinkedAccountForEmployee(emp, reason, note="") {
    const accs = await getAccounts();
    const targetAcc = accs.find(a =>
      String(a?.id||"") === String(emp?.linkedAccountId||"") ||
      (emp?.username && String(a?.username||"").trim().toLowerCase() === String(emp.username||"").trim().toLowerCase())
    );
    if(!targetAcc) return null;
    const targetRole = String(targetAcc?.role || "").trim().toLowerCase();
    if(targetRole && targetRole !== "solicitante") return { ...targetAcc, _preservedPrivilegedAccount: true };
    const deactivatedAt = new Date().toISOString();
    const updatedAccounts = ensureDefaultAdminAccount((Array.isArray(accs) ? accs : []).map(acc =>
      String(acc?.id||"") !== String(targetAcc?.id||"")
        ? acc
        : {
            ...acc,
            active:false,
            status:"Inativo",
            deactivatedAt,
            deactivatedBy:actorName,
            deactivationReason:String(reason||""),
            deactivationNote:String(note||""),
            updatedAt:deactivatedAt,
            updatedBy:actorName,
          }
    ), actorName);
    await saveAccounts(updatedAccounts);
    setAccounts(updatedAccounts);
    try {
      const res = await pushGlobal("auth:accounts", updatedAccounts, actorName);
      if(res?.version) await DB.set("auth:accounts:_ver", res.version);
    } catch(_) {}
    try {
      window.dispatchEvent(new CustomEvent("fz:sync:updated", { detail:{ key:"auth:accounts" } }));
    } catch(_) {}
    await appendUserDeletionLog({
      entityType:"employee_deactivated",
      role: targetAcc.role || "solicitante",
      accountId: targetAcc.id || "",
      linkedAccountId: targetAcc.id || emp?.linkedAccountId || "",
      employeeId: emp?.id || "",
      farmIds: [emp?.primaryFarmId || emp?.farmId || currentFarmId].filter(Boolean),
      name: emp?.name || targetAcc?.name || targetAcc?.username || "Colaborador",
      username: targetAcc?.username || emp?.username || "",
      reason: String(reason||""),
      note: String(note||""),
      deletedBy: actorName,
      deletedAt: deactivatedAt,
      snapshot: { employee: { ...emp }, account: { ...targetAcc } },
    }, actorName);
    return targetAcc;
  }

  async function reactivateLinkedAccountForEmployee(emp, reason, note="") {
    const accs = await getAccounts();
    const targetAcc = accs.find(a =>
      String(a?.id||"") === String(emp?.linkedAccountId||"") ||
      (emp?.username && String(a?.username||"").trim().toLowerCase() === String(emp.username||"").trim().toLowerCase())
    );
    if(!targetAcc) return null;
    const reactivatedAt = new Date().toISOString();
    const updatedAccounts = ensureDefaultAdminAccount((Array.isArray(accs) ? accs : []).map(acc =>
      String(acc?.id||"") !== String(targetAcc?.id||"")
        ? acc
        : {
            ...acc,
            active:true,
            status:"Ativo",
            reactivatedAt,
            reactivatedBy:actorName,
            reactivationReason:String(reason||""),
            reactivationNote:String(note||""),
            updatedAt:reactivatedAt,
            updatedBy:actorName,
          }
    ), actorName);
    await saveAccounts(updatedAccounts);
    setAccounts(updatedAccounts);
    try {
      const res = await pushGlobal("auth:accounts", updatedAccounts, actorName);
      if(res?.version) await DB.set("auth:accounts:_ver", res.version);
    } catch(_) {}
    try {
      window.dispatchEvent(new CustomEvent("fz:sync:updated", { detail:{ key:"auth:accounts" } }));
    } catch(_) {}
    return targetAcc;
  }

  async function reactivateEmployeeCascade(emp, reason, note="") {
    if(!emp?.id){ show("Colaborador não encontrado.", "error"); return; }
    if(!String(reason||"").trim()){ show("Selecione o motivo da reativação.", "error"); return; }
    const reactivatedAt = new Date().toISOString();
    const nextEmployees = (Array.isArray(employees) ? employees : []).map(e => String(e.id) !== String(emp.id) ? e : ({
      ...e,
      active:true,
      status:"Ativo",
      reactivatedAt,
      reactivatedBy:actorName,
      reactivationReason:String(reason||""),
      reactivationNote:String(note||""),
      deactivatedAt:"",
      deactivated_at:"",
      deactivatedBy:"",
      deactivationReason:"",
      deactivationNote:"",
      ...makeUpdateMeta(actorName, {
        active:true,
        status:"Ativo",
        reactivatedAt,
        reactivatedBy:actorName,
        reactivationReason:String(reason||""),
        reactivationNote:String(note||""),
        deactivatedAt:"",
        deactivated_at:"",
        deactivatedBy:"",
        deactivationReason:"",
        deactivationNote:"",
      })
    }));
    const linkedAcc = await reactivateLinkedAccountForEmployee(emp, reason, note);
    setEmployees(ensureEmployeeRecordNumbers(nextEmployees));
    await persistEmployeesAndConfirm(currentFarmId, nextEmployees, true);
    setReactivateFlow(null);
    setForm(null);
    show(linkedAcc ? "✅ Colaborador e usuário vinculado reativados." : "✅ Colaborador reativado.");
  }

  async function deactivateEmployeeCascade(emp, reason, note="") {
    if(!emp?.id){ show("Colaborador não encontrado.", "error"); return; }
    if(!String(reason||"").trim()){ show("Selecione o motivo da desativação.", "error"); return; }
    const deactivatedAt = new Date().toISOString();
    const nextEmployees = (Array.isArray(employees) ? employees : []).map(e => String(e.id) !== String(emp.id) ? e : ({ ...e, active:false, status:"Inativo", deactivatedAt, deactivated_at:deactivatedAt, deactivatedBy:actorName, deactivationReason:String(reason||""), deactivationNote:String(note||""), ...makeUpdateMeta(actorName, { active:false, status:"Inativo", deactivatedAt, deactivated_at:deactivatedAt, deactivatedBy:actorName, deactivationReason:String(reason||""), deactivationNote:String(note||"") }) }));
    const linkedAcc = await deactivateLinkedAccountForEmployee(emp, reason, note);
    setEmployees(ensureEmployeeRecordNumbers(nextEmployees));
    await persistEmployeesAndConfirm(currentFarmId, nextEmployees, true);
    if(!linkedAcc){
      await appendUserDeletionLog({
        entityType:"employee_deactivated",
        role:"solicitante",
        accountId:"",
        linkedAccountId: emp?.linkedAccountId || "",
        employeeId: emp?.id || "",
        farmIds: [emp?.primaryFarmId || emp?.farmId || currentFarmId].filter(Boolean),
        name: emp?.name || "Colaborador",
        username: emp?.username || "",
        reason: String(reason||""),
        note: String(note||""),
        deletedBy: actorName,
        deletedAt: deactivatedAt,
        snapshot: { employee: { ...emp } },
      }, actorName);
    }
    setDeleteFlow(null);
    setForm(null);
    if(linkedAcc?._preservedPrivilegedAccount){
      show("⛔ Colaborador desativado. Conta admin/gestor vinculada foi preservada.");
    } else {
      show(linkedAcc ? "⛔ Colaborador e usuário vinculado desativados." : "⛔ Colaborador desativado.");
    }
  }

  const normalizeIdentityValue = (value="") => String(value || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim();

  const employeeMatchesRecord = (emp, record) => {
    if(!emp || !record) return false;
    const employeeIds = [emp?.id, emp?.employeeId, emp?.linkedAccountId, emp?.accountId, emp?.userId].map(v=>String(v||"").trim()).filter(Boolean);
    const collectCandidates = (obj, acc=[]) => {
      if(!obj) return acc;
      if(Array.isArray(obj)) return obj.reduce((out, item)=>collectCandidates(item, out), acc);
      if(typeof obj !== "object") {
        acc.push({ name: obj, username: "", id: "", employeeId: "", linkedAccountId: "", accountId: "", userId: "" });
        return acc;
      }
      acc.push(obj);
      const nestedKeys = ["assignees","participants","responsibles","assignedTo","assignedToId","assignedToName","contactPerson","contactPersonId","requester","requestedBy","requestedByName","employee","employeeId","employeeName","collaborator","collaboratorId","collaboratorName","user","userId","owner","ownerId","createdBy","updatedBy"];
      nestedKeys.forEach(key=>{
        if(obj[key]!==undefined) collectCandidates(obj[key], acc);
      });
      return acc;
    };
    const candidates = collectCandidates(record, []);
    return candidates.some(obj=>{
      const ids = [obj?.id, obj?.employeeId, obj?.linkedAccountId, obj?.accountId, obj?.userId].map(v=>String(v||"").trim()).filter(Boolean);
      if(ids.some(v=>employeeIds.includes(v))) return true;
      const usernames = [obj?.username, obj?.login].map(v=>normalizeIdentityValue(v)).filter(Boolean);
      if(usernames.some(v=>v===normalizeIdentityValue(emp?.username||""))) return true;
      const names = [obj?.name, obj?.displayName, obj?.employeeName, obj?.requester, obj?.createdBy, obj?.updatedBy].map(v=>normalizeIdentityValue(v)).filter(Boolean);
      return names.some(v=>v===normalizeIdentityValue(emp?.name||""));
    });
  };

  const buildEmployeeHistory = (emp) => {
    const activeOnly = (item) => item && !item._deleted && !item.deletedAt && !item.deleted_at;
    const serviceItems = (Array.isArray(services) ? services : []).filter(activeOnly).filter(item=>employeeMatchesRecord(emp, item)).map(item=>({
      id:item.id,
      type:"Serviço",
      title:item.title || item.serviceType || item.subject || item.name || "Serviço sem título",
      status:item.status || "—",
      date:item.date || item.createdAt || item.updatedAt || "",
      costCenter:item.costCenter || "",
    }));
    const occurrenceItems = (Array.isArray(occurrences) ? occurrences : []).filter(activeOnly).filter(item=>employeeMatchesRecord(emp, item)).map(item=>({
      id:item.id,
      type:"Ocorrência",
      title:item.title || item.subject || item.type || item.name || "Ocorrência sem título",
      status:item.status || "—",
      date:item.date || item.createdAt || item.updatedAt || "",
      costCenter:item.costCenter || "",
    }));
    const inspectionItems = (Array.isArray(inspections) ? inspections : []).filter(activeOnly).filter(item=>employeeMatchesRecord(emp, item)).map(item=>({
      id:item.id,
      type:"Vistoria",
      title:item.title || item.targetName || item.locationLabel || item.type || "Vistoria",
      status:item.status || item.result || "—",
      date:item.date || item.createdAt || item.updatedAt || "",
      costCenter:item.costCenter || "",
    }));
    const orderItems = (Array.isArray(orders) ? orders : []).filter(activeOnly).filter(item=>employeeMatchesRecord(emp, item)).map(item=>({
      id:item.id,
      type:"Compra",
      title:item.title || item.subject || item.category || `Pedido ${item.id || ""}`,
      status:item.status || "—",
      date:item.date || item.createdAt || item.updatedAt || "",
      costCenter:item.costCenter || "",
    }));
    const timeline = [...serviceItems, ...occurrenceItems, ...inspectionItems, ...orderItems]
      .sort((a,b)=>String(b.date||"").localeCompare(String(a.date||"")));
    return {
      employee: emp,
      services: serviceItems,
      occurrences: occurrenceItems,
      inspections: inspectionItems,
      orders: orderItems,
      timeline,
    };
  };

  async function save(){

    if(form?._new && !canCreate){show("Sem permissão para criar colaboradores.","error");return;}
    if(!form?._new && !canEdit){show("Sem permissão para editar colaboradores.","error");return;}
    if(!String(form.firstName || "").trim()){show("Nome obrigatório.","error");return;}
    if(!String(form.lastName || "").trim()){show("Sobrenome obrigatório.","error");return;}
    if(!String(form.birthDate || "").trim()){show("Data de nascimento obrigatória.","error");return;}
    if(!currentFarmId){show("Erro: fazenda não identificada. Tente novamente.","error");return;}
    if(form.username?.trim()){
      const normalizedFormUsername = normalizeLinkUsername(form.username);
      const dup = employeesView.find(e=>e.id!==form.id && normalizeLinkUsername(e?.username||"")===normalizedFormUsername);
      if(dup){show("Usuário já existe para outro colaborador.","error");return;}
      const usernameState = getUsernameAvailability(form.username, accounts, form?.linkedAccountId || "", reservedUsernames);
      if(!usernameState.available){
        show("Nome de usuário já foi utilizado no sistema. Escolha outro.","error");
        return;
      }
    }

    const { _new, usernameAuto:_discardUsernameAuto, existingAccounts:_discardExistingAccounts, reservedUsernames:_discardReservedUsernames, ...raw } = form;
    const baseEmployees = Array.isArray(employeesView) ? employeesView : [];
    const existingEmployee = !_new ? baseEmployees.find(e => String(e.id) === String(raw.id)) : null;
    const nextEmployeeId = String(existingEmployee?.id || raw.id || createClientId("emp"));
    const d = { ...raw, id: nextEmployeeId };
    if(_new) d.primaryFarmId = currentFarmId;
    d.farmId = d.primaryFarmId || currentFarmId || d.farmId || "";

    const targetFarmId = d.primaryFarmId || currentFarmId;
    const isMovingFarm = !_new && targetFarmId && targetFarmId !== currentFarmId;
    const allEmpFarmIds = [
      targetFarmId,
      ...(d.sharedFarms || []).map(sf => sf.farmId),
    ].filter(Boolean);

    const accs = await getAccounts();
    let linkedAccountId = d.linkedAccountId || existingEmployee?.linkedAccountId || null;
    let accountsUpdated = null;
    let generatedUsername = "";
    let generatedPassword = "";

    const reservedAccountIds = linkedAccountId ? [String(linkedAccountId)] : [];
    const requestedUsername = String(form.username || existingEmployee?.username || "").trim();
    const normalizedRequestedUsername = requestedUsername.toLowerCase();
    const linkedAccount = linkedAccountId ? accs.find(a => String(a?.id||"") === String(linkedAccountId)) : null;
    const existingAcc = linkedAccount || accs.find(a =>
      (linkedAccountId && String(a?.id||"") === String(linkedAccountId)) ||
      (normalizedRequestedUsername && String(a?.username||"").trim().toLowerCase() === normalizedRequestedUsername)
    );

    const requestedPassword = String(form.password || "").trim();
    if(requestedPassword && !isValidAccessPassword(requestedPassword)){
      show("A senha deve ter de 4 a 30 caracteres. Pode conter letras, números e caracteres especiais.","error");
      return;
    }
    const generatedDefaultUsername = buildEmployeeLoginUsername(d.firstName || d.name || "colaborador", "", accs, reservedAccountIds, reservedUsernames);
    const usernameState = getUsernameAvailability(requestedUsername || generatedDefaultUsername, accs, existingAcc?.id || linkedAccountId || "", reservedUsernames);
    if(requestedUsername && !usernameState.available){
      show("Nome de usuário já foi utilizado no sistema. Escolha outro.","error");
      return;
    }
    const finalUsername = requestedUsername || existingAcc?.username || generatedDefaultUsername;
    if(!requestedUsername) generatedUsername = finalUsername;
    const finalPassword = requestedPassword || existingAcc?.password || buildTemporaryAccessPassword();
    if(!requestedPassword && !existingAcc?.password) generatedPassword = finalPassword;
    const preservedAccountRole = String(existingAcc?.role || "").trim() || "solicitante";
    const preservedIsPrivilegedAccount = preservedAccountRole.toLowerCase() === "admin" || preservedAccountRole.toLowerCase() === "gestor";

    linkedAccountId = existingAcc?.id || linkedAccountId || createClientId("usr");
    accountsUpdated = (Array.isArray(accs) ? accs : []).some(a => String(a?.id||"")===String(linkedAccountId))
      ? accs.map(a => String(a?.id||"") !== String(linkedAccountId)
          ? a
          : {
              ...a,
              id: linkedAccountId,
              name: d.name,
              firstName: String((d.name||"").split(" ")[0] || a?.firstName || "").trim(),
              lastName: String((d.name||"").split(" ").slice(1).join(" ") || a?.lastName || "").trim(),
              username: finalUsername,
              password: finalPassword,
              role: preservedAccountRole,
              active: preservedIsPrivilegedAccount ? true : d.status !== "Inativo",
              farmIds: [...new Set([...(a?.farmIds||[]), ...allEmpFarmIds])],
              allowedModules: Array.isArray(a?.allowedModules) ? a.allowedModules : [],
              allowedCards: Array.isArray(a?.allowedCards) ? a.allowedCards : [],
              modulePermissions: (a?.modulePermissions && typeof a.modulePermissions === "object") ? a.modulePermissions : undefined,
              costCenterPermissions: (a?.costCenterPermissions && typeof a.costCenterPermissions === "object") ? a.costCenterPermissions : undefined,
              canManagePurchases: ["admin","gestor"].includes(preservedAccountRole.toLowerCase()) ? true : !!a?.canManagePurchases,
              canApprovePurchaseWithoutQuote: preservedAccountRole.toLowerCase() === "admin" ? true : !!a?.canApprovePurchaseWithoutQuote,
              uiPreferences: a?.uiPreferences,
              collabLevel: d.collabLevel || a?.collabLevel || 1,
              email: d.email || a?.email || "",
              phone: d.phone || a?.phone || "",
              employeeId: d.id,
              userId: String(a?.userId || "").trim() || createClientId("usr"),
              mustChangePassword: requestedPassword ? !!a?.mustChangePassword : true,
              temporaryPassword: requestedPassword ? !!a?.temporaryPassword : true,
              updatedAt: new Date().toISOString(),
              updatedBy: session?.name || "admin",
            })
      : [...accs, {
          id: linkedAccountId,
          username: finalUsername,
          password: finalPassword,
          name: d.name,
          firstName: String((d.name||"").split(" ")[0] || "").trim(),
          lastName: String((d.name||"").split(" ").slice(1).join(" ") || "").trim(),
          role: "solicitante",
          active: d.status !== "Inativo",
          farmIds: [...new Set(allEmpFarmIds)],
          allowedModules: [],
          allowedCards: [],
          modulePermissions: undefined,
          costCenterPermissions: undefined,
          collabLevel: d.collabLevel || 1,
          email: d.email || "",
          phone: d.phone || "",
          employeeId: d.id,
          userId: createClientId("usr"),
          mustChangePassword: true,
          temporaryPassword: true,
          ...makeCreateMeta(session?.name||"admin"),
        }];

    if(linkedAccountId){
      d.linkedAccountId = linkedAccountId;
      const ensuredAccount = (Array.isArray(accountsUpdated) ? accountsUpdated : []).find(a => String(a?.id||"") === String(linkedAccountId));
      d.userId = String(ensuredAccount?.userId || d.userId || linkedAccountId || "").trim();
      d.username = finalUsername;
    }

    if(isMovingFarm){
      const currentKey = `fz:${currentFarmId}:employees`;
      const nextCurrent = baseEmployees.filter(e => String(e.id) !== String(d.id));
      setEmployees(ensureEmployeeRecordNumbers(nextCurrent));
      await DB.set(currentKey, nextCurrent);
      try {
        window.dispatchEvent(new CustomEvent("fz:sync:updated", { detail: { key: currentKey, source: "local-write" } }));
      } catch {}

      const tgtKey = `fz:${targetFarmId}:employees`;
      const tgtList = (await DB.get(tgtKey)) || [];
      const alreadyThere = tgtList.find(e => String(e.id) === String(d.id) || String(e.linkedAccountId || "") === String(linkedAccountId || ""));
      const movedBase = alreadyThere
        ? { ...alreadyThere, ...d, id: String(alreadyThere.id), clientId: d.clientId || alreadyThere.clientId || alreadyThere.id, farmId: targetFarmId || d.farmId || "", primaryFarmId: targetFarmId || d.primaryFarmId || currentFarmId || "", ...makeUpdateMeta(actorName) }
        : ensureOperationalRecordNumber({ ...(existingEmployee || {}), ...d, id: String(existingEmployee?.id || d.id || createClientId("emp")), clientId: d.clientId || existingEmployee?.clientId || d.id || createClientId("emp"), farmId: targetFarmId || d.farmId || "", primaryFarmId: targetFarmId || d.primaryFarmId || currentFarmId || "", ...makeCreateMeta(actorName) }, tgtList, "employees");
      const movedRecord = alreadyThere
        ? markOperationalUpdatePending(movedBase, { userName: actorName })
        : markOperationalCreatePending(movedBase, { userName: actorName });
      const nextTarget = alreadyThere
        ? tgtList.map(e => (String(e.id) === String(alreadyThere.id) ? movedRecord : e))
        : [...tgtList, movedRecord];
      await DB.set(tgtKey, nextTarget);
      try {
        window.dispatchEvent(new CustomEvent("fz:sync:updated", { detail: { key: tgtKey, source: "local-write" } }));
      } catch {}

      if(Array.isArray(accountsUpdated)){
        const ensuredAccounts = ensureDefaultAdminAccount(accountsUpdated);
        await saveAccounts(ensuredAccounts);
        setAccounts(ensuredAccounts);
        try {
          const res = await pushGlobal("auth:accounts", ensuredAccounts, session?.name||"admin");
          if (res?.version) await DB.set("auth:accounts:_ver", res.version);
        } catch(e) {}
        try {
          window.dispatchEvent(new CustomEvent("fz:sync:updated", { detail: { key: "auth:accounts" } }));
        } catch {}
      }

      syncOnSave(currentFarmId, "employees");
      syncOnSave(targetFarmId,  "employees");

      const tgtFarm = (allFarms||[]).find(f=>f.id===targetFarmId);
      show(`✅ ${d.name} movido para ${tgtFarm?.name||targetFarmId}!`);
    } else {
      const baseSavedRecord = _new
        ? ensureOperationalRecordNumber({ ...d, id: nextEmployeeId, clientId: d.clientId || nextEmployeeId, farmId: d.farmId || currentFarmId || "", primaryFarmId: d.primaryFarmId || currentFarmId || "", ...makeCreateMeta(actorName) }, baseEmployees, "employees")
        : { ...(existingEmployee || {}), ...d, id: nextEmployeeId, clientId: d.clientId || existingEmployee?.clientId || nextEmployeeId, farmId: d.farmId || existingEmployee?.farmId || currentFarmId || "", primaryFarmId: d.primaryFarmId || existingEmployee?.primaryFarmId || currentFarmId || "", ...makeUpdateMeta(actorName) };
      const savedRecord = _new
        ? markOperationalCreatePending(baseSavedRecord, { userName: actorName })
        : markOperationalUpdatePending(baseSavedRecord, { userName: actorName });
      const nextEmployees = _new
        ? [...baseEmployees, savedRecord]
        : baseEmployees.map(e => String(e.id) === String(savedRecord.id) ? savedRecord : e);

      let finalEmployees = nextEmployees;
      setEmployees(ensureEmployeeRecordNumbers(finalEmployees));
      await persistEmployeesAndConfirm(currentFarmId, finalEmployees, true);

      if(Array.isArray(accountsUpdated)){
        const ensuredAccounts = ensureDefaultAdminAccount(accountsUpdated);
        await saveAccounts(ensuredAccounts);
        setAccounts(ensuredAccounts);
        try {
          const res = await pushGlobal("auth:accounts", ensuredAccounts, session?.name||"admin");
          if (res?.version) await DB.set("auth:accounts:_ver", res.version);
        } catch(e) {}
        try {
          window.dispatchEvent(new CustomEvent("fz:sync:updated", { detail: { key: "auth:accounts" } }));
        } catch {}
      }
    }

    const accessNotes = [];
    if(generatedUsername) accessNotes.push(`usuário: ${generatedUsername}`);
    if(generatedPassword) accessNotes.push(`senha temporária: ${generatedPassword}`);
    setEditingFlag(false);
    show(accessNotes.length ? `✅ Salvo! Conta vinculada criada automaticamente (${accessNotes.join(" · ")}).` : "✅ Salvo!");
    setForm(null);
  }


  // Can gestor see a restricted field?
  function gestorCanSee(emp, field){
    return (emp.gestorVisibleFields||[]).includes(field);
  }

  // Summary counts
  const totalEpis  = checklists.reduce((s,c)=>s+c.items.filter(i=>i.type==="EPI"&&i.withEmployee).length,0);
  const totalEquips= checklists.reduce((s,c)=>s+c.items.filter(i=>i.type==="Equipamento"&&i.withEmployee).length,0);

  // Table columns — gestor sees restricted fields only if allowed per employee
  const tableCols = [
    {key:"name", label:"Nome", render:r=>{
      const isPrimary=!r.primaryFarmId||r.primaryFarmId===currentFarmId;
      const sharedHere=(r.sharedFarms||[]).find(sf=>sf.farmId===currentFarmId);
      const primaryFarm=!isPrimary?(allFarms||[]).find(f=>f.id===r.primaryFarmId):null;
      const lvl=COLLAB_LEVELS.find(l=>l.id===(r.collabLevel||1));
      return(
        <div>
          <div style={{fontWeight:600,color:T.text,display:"flex",alignItems:"center",gap:6}}>
            {r.name}
            {lvl&&<span style={{fontSize:10,background:`${lvl.color}22`,color:lvl.color,borderRadius:4,padding:"1px 6px",fontWeight:700}}>{lvl.icon} {lvl.label}</span>}
          </div>
          {r.username&&<div style={{fontSize:10,color:T.accent}}>@{r.username}</div>}
          {r.status==="Inativo"&&<div style={{marginTop:4}}><button onClick={(e)=>{ e.stopPropagation(); setEmployeeHistory(buildEmployeeHistory(r)); }} style={{background:"none",border:`1px solid ${T.accent}`,borderRadius:999,padding:"3px 8px",color:T.accent,fontSize:10,fontWeight:700,cursor:"pointer"}}>Histórico</button></div>}
          {!isPrimary&&primaryFarm&&(
            <div style={{fontSize:10,marginTop:2}}>
              <span style={{background:`${T.yellow}22`,color:T.yellow,borderRadius:4,padding:"1px 5px",border:`1px solid ${T.yellow}44`}}>
                🔀 Compartilhado de <FarmInlineLabel farm={primaryFarm} size={12} textStyle={{fontWeight:700}} /> · {sharedHere?.pct||0}%
              </span>
            </div>
          )}
          {isPrimary&&(r.sharedFarms||[]).length>0&&(
            <div style={{fontSize:10,marginTop:2}}>
              <span style={{background:`${T.accent}18`,color:T.accent,borderRadius:4,padding:"1px 5px",border:`1px solid ${T.accent}33`}}>
                🌾 Atua em {(r.sharedFarms||[]).length} fazenda(s) adicional(ais)
              </span>
            </div>
          )}
        </div>
      );
    }},
    {key:"role",      label:"Cargo",    render:r=><Badge color={T.muted}>{r.role}</Badge>},
    {key:"costCenter",label:"Centro",   render:r=><CCBadge cc={r.costCenter}/>},
    {key:"phone",     label:"Telefone", render:r=><span style={{color:T.muted}}>{r.phone||"—"}</span>},
    ...(isAdmin||true ? [{key:"status", label:"Status", render:r=><Badge color={r.status==="Ativo"?T.green:r.status==="Férias"?T.yellow:T.red}>{r.status}</Badge>}] : []),
    ...(isAdmin ? [
      {key:"salary", label:"Salário",  render:r=><span>R$ {fmtMoney(r.salary||0)}</span>},
      {key:"hired",  label:"Admissão", render:r=><span style={{color:T.muted}}>{r.hired?fmtDate(r.hired):"—"}</span>},
    ] : []),
    ...(!isAdmin && isGestor ? [
      {key:"salary", label:"Salário",  render:r=>gestorCanSee(r,"salary")?<span>R$ {fmtMoney(r.salary||0)}</span>:<span style={{color:T.border}}>🔒</span>},
      {key:"hired",  label:"Admissão", render:r=>gestorCanSee(r,"hired")?<span style={{color:T.muted}}>{r.hired?fmtDate(r.hired):"—"}</span>:<span style={{color:T.border}}>🔒</span>},
    ] : []),
  ];

  const activeEmployeesCount = employeesView.filter(e=>(e.status||"Ativo")!=="Inativo").length;
  const epiConferenceCount = (checklists||[]).reduce((acc, cl)=>acc + ((cl?.conferences||[]).filter(conf=>(conf?.items||[]).some(item=>String(item?.itemType||"") === "EPI")).length), 0);
  const equipmentConferenceCount = (checklists||[]).reduce((acc, cl)=>acc + ((cl?.conferences||[]).filter(conf=>(conf?.items||[]).some(item=>String(item?.itemType||"") === "Equipamento")).length), 0);
  const sectionAllowedCards = tab === "chk" ? allowedItemsMenuCards : (tab === "emp" ? allowedEmployeesMenuCards : allowedRootCards);
  const summaryCards = [
    { id:"numero_colaboradores", icon:"👥", label:"Número de Colaboradores", sub:"ativos", value:activeEmployeesCount, color:T.green, iconStyle:{fontSize:26} },
    { id:"equipamentos_entregues", icon:"📻", label:"Número de Equipamentos Entregues", value:totalEquips, color:T.accent, iconStyle:{fontSize:25} },
    { id:"epis_entregues", icon:"🦺", label:"Número de EPIs Entregues", value:totalEpis, color:T.green, iconStyle:{fontSize:26} },
    { id:"conferencia_epis", icon:"📝", label:"Conferência de EPIs", value:epiConferenceCount, color:T.purple, iconStyle:{fontSize:25} },
    { id:"conferencia_equipamentos", icon:"🧰", label:"Conferência de Equipamentos", value:equipmentConferenceCount, color:T.yellow, iconStyle:{fontSize:25} },
  ].filter(card=>sectionAllowedCards.includes(card.id));


  const renderEmployeeInlineEditor = (extraStyle={}) => form && <div style={{marginTop:18,background:T.card,border:`1px solid ${T.border}`,borderRadius:18,padding:isCompactViewport()?16:20,boxShadow:`0 12px 28px ${T.shadow || "rgba(0,0,0,.08)"}`,...extraStyle}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:isCompactViewport()?"flex-start":"center",gap:12,flexWrap:"wrap",marginBottom:14}}>
          <div>
            <div style={{fontSize:11,fontWeight:800,color:T.accent,textTransform:"uppercase",letterSpacing:.8}}>Fluxo inline</div>
            <div style={{fontSize:isCompactViewport()?18:20,fontWeight:800,color:T.text,marginTop:4}}>{form._new?"Novo Colaborador(a)":"Editar Colaborador(a)"}</div>
            <div style={{fontSize:12,color:T.muted,marginTop:4}}>Faça ajustes sem sair da listagem de colaboradores.</div>
          </div>
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
            <Btn variant="ghost" onClick={()=>{setForm(null);setEditingFlag(false);}}>Fechar editor</Btn>
            {((form&&form._new&&canCreate)||(form&&!form._new&&canEdit))&&<Btn onClick={save}>Salvar agora</Btn>}
          </div>
        </div>
        {form&&<div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"1fr 1fr",gap:14}}>

          {/* ── Dados básicos (todos veem) ── */}
          <div style={{gridColumn:"1/-1",fontSize:11,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:.8,borderBottom:`1px solid ${T.border}`,paddingBottom:6}}>
            👤 Dados Básicos
          </div>
          <Field label="Nome *" col={isCompactViewport()?"1/-1":undefined}><Input value={form.firstName||""} onChange={e=>updateEmployeeNameField("firstName", e.target.value)} onBlur={refreshEmployeeAccessSuggestion} placeholder="Primeiro nome"/></Field>
          <Field label="Sobrenome *" col={isCompactViewport()?"1/-1":undefined}><Input value={form.lastName||""} onChange={e=>updateEmployeeNameField("lastName", e.target.value)} placeholder="Sobrenome"/></Field>
          <Field label="Cargo"><Select value={form.role||FUNC_ROLES[0]} onChange={e=>setForm(f=>({...f,role:e.target.value}))}>{(SL.FUNC_ROLES||FUNC_ROLES).map(r=><option key={r}>{r}</option>)}</Select></Field>
          <Field label="Centro"><Select value={form.costCenter||COST_CENTERS[0]} onChange={e=>setForm(f=>({...f,costCenter:e.target.value}))}>{(SL.COST_CENTERS||COST_CENTERS).map(c=><option key={c}>{c}</option>)}</Select></Field>
          <Field label="Status"><Select value={form.status||"Ativo"} onChange={e=>setForm(f=>({...f,status:e.target.value}))}>{(SL.FUNC_STATUS||FUNC_STATUS).map(s=><option key={s}>{s}</option>)}</Select></Field>
          <Field label="Telefone"><Input value={form.phone||""} onChange={e=>setForm(f=>({...f,phone:e.target.value}))} placeholder="(00) 00000-0000"/></Field>
          <Field label="E-mail"><Input value={form.email||""} onChange={e=>setForm(f=>({...f,email:e.target.value}))} placeholder="nome@exemplo.com"/></Field>

          {/* ── Acesso ao sistema (admin cria credenciais) ── */}
          {isAdmin&&<>
            <div style={{gridColumn:"1/-1",fontSize:11,fontWeight:700,color:T.accent,textTransform:"uppercase",letterSpacing:.8,borderBottom:`1px solid ${T.border}`,paddingBottom:6,marginTop:8}}>
              🔑 Acesso ao Sistema
            </div>
            <Field label="Usuário (login)">
              <Input value={form.username||""} onChange={e=>setForm(f=>({...f,username:e.target.value,usernameAuto:false}))} placeholder="Ex: joao"/>
              <div style={{fontSize:10,color:T.muted,marginTop:3}}>Preenchimento automático pelo primeiro nome. Se já existir, usa nome + ano de nascimento.</div>
              {!!String(form.username||"").trim() && !!usernameAvailability && (
                <div style={{fontSize:10,marginTop:4,color:usernameAvailability.available ? T.green : T.red,fontWeight:600}}>
                  {usernameAvailability.available
                    ? `Usuário disponível: @${usernameAvailability.normalized}`
                    : `Usuário indisponível: @${usernameAvailability.normalized}. Esse nome já foi utilizado no sistema.`}
                </div>
              )}
            </Field>
            <Field label={form._new?"Senha":"Nova Senha (deixe vazio p/ não alterar)"}>
              <PwInput value={form.password||""} onChange={e=>setForm(f=>({...f,password:e.target.value}))} placeholder={form._new?"1234":"Sem alteração"}/>
              <div style={{fontSize:10,color:T.muted,marginTop:3}}>No primeiro acesso o usuário será obrigado a trocar a senha. Senhas aceitam 4 a 30 caracteres, incluindo números e caracteres especiais.</div>
            </Field>
          </>}

          {/* ── Dados pessoais ── */}
          <div style={{gridColumn:"1/-1",fontSize:11,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:.8,borderBottom:`1px solid ${T.border}`,paddingBottom:6,marginTop:8}}>
            📋 Dados Pessoais
          </div>
          <Field label="CPF"><Input value={form.cpf||""} onChange={e=>setForm(f=>({...f,cpf:e.target.value}))} placeholder="000.000.000-00"/></Field>
          <Field label="RG / Identidade"><Input value={form.rg||""} onChange={e=>setForm(f=>({...f,rg:e.target.value}))} placeholder="00.000.000-0"/></Field>
          <Field label="Data de Nascimento *"><Input type="date" value={form.birthDate||""} onChange={e=>setForm(f=>({...f,birthDate:e.target.value}))}/></Field>
          <Field label="E-mail"><Input type="email" value={form.email||""} onChange={e=>setForm(f=>({...f,email:e.target.value}))} placeholder="email@exemplo.com"/></Field>
          <Field label="Chave PIX" col="1/-1"><Input value={form.pix||""} onChange={e=>setForm(f=>({...f,pix:e.target.value}))} placeholder="CPF, telefone, e-mail ou chave aleatória"/></Field>

          {/* ── Nível do Colaborador (admin only) ── */}
          {isAdmin&&<>
            <div style={{gridColumn:"1/-1",fontSize:11,fontWeight:700,color:T.purple,textTransform:"uppercase",letterSpacing:.8,borderBottom:`1px solid ${T.border}`,paddingBottom:6,marginTop:8}}>
              ⭐ Nível de Acesso
            </div>
            <Field label="" col="1/-1">
              <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8}}>
                {COLLAB_LEVELS.map(lv=>(
                  <div key={lv.id}
                    onClick={()=>setForm(f=>({...f,collabLevel:lv.id}))}
                    style={{padding:"10px 14px",borderRadius:10,cursor:"pointer",textAlign:"center",
                      border:`2px solid ${form.collabLevel===lv.id?lv.color:T.border}`,
                      background:form.collabLevel===lv.id?`${lv.color}18`:T.card2,
                      transition:"all .15s"}}>
                    <div style={{fontSize:22,marginBottom:4}}>{lv.icon}</div>
                    <div style={{fontWeight:700,color:form.collabLevel===lv.id?lv.color:T.muted,fontSize:13}}>{lv.label}</div>
                    <div style={{fontSize:10,color:T.muted,marginTop:3,lineHeight:1.4}}>{lv.desc}</div>
                  </div>
                ))}
              </div>
            </Field>
          </>}

          {/* ── Dados restritos (admin e gestor se autorizado) ── */}
          {(isAdmin||(isGestor))&&<>
            <div style={{gridColumn:"1/-1",fontSize:11,fontWeight:700,color:T.red,textTransform:"uppercase",letterSpacing:.8,borderBottom:`1px solid ${T.border}`,paddingBottom:6,marginTop:8}}>
              🔒 Dados Restritos {!isAdmin&&"(visibilidade controlada pelo Admin)"}
            </div>
            {(isAdmin||gestorCanSee(form,"salary"))&&
              <Field label="Salário (R$)">
                <div style={{position:"relative"}}>
                  <span style={{position:"absolute",left:10,top:"50%",transform:"translateY(-50%)",color:T.muted,fontSize:13,pointerEvents:"none"}}>R$</span>
                  <Input type="number" value={form.salary||0} step="0.01" min="0"
                    onChange={e=>setForm(f=>({...f,salary:Number(e.target.value)}))}
                    disabled={!isAdmin}
                    style={{paddingLeft:30}}/>
                </div>
              </Field>}
            {(isAdmin||gestorCanSee(form,"hired"))&&
              <Field label="Data de Admissão"><Input type="date" value={form.hired||""} onChange={e=>setForm(f=>({...f,hired:e.target.value}))} disabled={!isAdmin}/></Field>}
            {(isAdmin||gestorCanSee(form,"notes"))&&
              <Field label="Observações Internas" col="1/-1"><Textarea rows={2} value={form.notes||""} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} disabled={!isAdmin} placeholder="Observações visíveis apenas internamente..."/></Field>}
          </>}

          {/* ── Transferência de Fazenda (admin only) ── */}
          {isAdmin&&!form._new&&<>
            <div style={{gridColumn:"1/-1",fontSize:11,fontWeight:700,color:T.accent,textTransform:"uppercase",letterSpacing:.8,borderBottom:`1px solid ${T.border}`,paddingBottom:6,marginTop:8}}>
              🔀 Transferência de Fazenda
            </div>
            <Field label="" col="1/-1">
              {(()=>{
                const currentPrimary = form.primaryFarmId || currentFarmId;
                const isSameFarm = currentPrimary === currentFarmId;
                const targetFarm = (activeFarms||[]).find(f=>f.id===currentPrimary);
                const originFarm = (activeFarms||[]).find(f=>f.id===currentFarmId);
                return(
                  <div style={{background:T.card2,borderRadius:12,padding:"14px 16px",border:`2px solid ${isSameFarm?T.border:T.accent}`}}>
                    <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:10}}>
                      Fazenda atual do colaborador:
                      <span style={{marginLeft:8,color:T.accent}}><FarmInlineLabel farm={originFarm} name={originFarm?.name||currentFarmId} size={14} textStyle={{color:T.accent,fontWeight:700}} /></span>
                    </div>
                    <div style={{fontSize:12,color:T.muted,marginBottom:10}}>
                      Para transferir para outra fazenda, selecione abaixo e clique em Salvar:
                    </div>
                    <Select value={form.primaryFarmId||currentFarmId}
                      onChange={e=>setForm(f=>({...f,primaryFarmId:e.target.value}))}>
                      {(activeFarms||[]).map(f=>(
                        <option key={f.id} value={f.id}>{f.name}{f.id===currentFarmId?" (atual)":""}</option>
                      ))}
                    </Select>
                    {!isSameFarm&&(
                      <div style={{marginTop:10,background:`${T.accent}15`,border:`1px solid ${T.accent}44`,borderRadius:8,padding:"10px 12px",fontSize:12,color:T.accent,fontWeight:600}}>
                        ⚠️ Ao salvar, <strong>{form.name}</strong> será transferido de <strong>{originFarm?.name}</strong> para <strong>{targetFarm?.name||currentPrimary}</strong>.
                        O registro será movido para a nova fazenda.
                      </div>
                    )}
                  </div>
                );
              })()}
            </Field>
          </>}

          {/* ── Multi-fazenda — atuação em fazendas adicionais (admin only) ── */}
          {isAdmin&&<>
            <div style={{gridColumn:"1/-1",fontSize:11,fontWeight:700,color:T.accent,textTransform:"uppercase",letterSpacing:.8,borderBottom:`1px solid ${T.border}`,paddingBottom:6,marginTop:8}}>
              🌾 Fazendas de Atuação Adicional
            </div>
            <Field label="" col="1/-1">
              <div style={{display:"flex",flexDirection:"column",gap:10}}>
                <div style={{background:T.card2,borderRadius:10,padding:"12px 14px"}}>
                  <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:8}}>
                    📋 Fazenda do Contrato <span style={{fontSize:11,color:T.muted,fontWeight:400}}>(para cálculo salarial)</span>
                  </div>
                  <Select value={form.primaryFarmId||currentFarmId}
                    onChange={e=>setForm(f=>({...f,primaryFarmId:e.target.value}))}>
                    {(activeFarms||[]).map(f=>(
                      <option key={f.id} value={f.id}>{f.name}</option>
                    ))}
                  </Select>
                </div>

                {/* Shared farms */}
                <div style={{background:T.card2,borderRadius:10,padding:"12px 14px"}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                    <div style={{fontSize:12,fontWeight:700,color:T.text}}>
                      🔀 Também atua em outras fazendas
                    </div>
                    <Btn size="sm" variant="ghost" onClick={()=>{
                      const others=(activeFarms||[]).filter(f=>f.id!==(form.primaryFarmId||currentFarmId)&&!(form.sharedFarms||[]).find(sf=>sf.farmId===f.id));
                      if(others.length>0) setForm(f=>({...f,sharedFarms:[...(f.sharedFarms||[]),{farmId:others[0].id,pct:0}]}));
                    }}>+ Adicionar fazenda</Btn>
                  </div>
                  {(form.sharedFarms||[]).length===0&&(
                    <div style={{fontSize:12,color:T.muted,fontStyle:"italic"}}>Nenhuma — atua somente na fazenda do contrato</div>
                  )}
                  {(form.sharedFarms||[]).map((sf,i)=>{
                    return(
                      <div key={i} style={{display:"grid",gridTemplateColumns:"1fr 100px 36px",gap:8,alignItems:"center",marginBottom:6}}>
                        <Select value={sf.farmId} onChange={e=>setForm(f=>({...f,sharedFarms:f.sharedFarms.map((x,j)=>j===i?{...x,farmId:e.target.value}:x)}))}>
                          {(activeFarms||[]).filter(f=>f.id!==(form.primaryFarmId||currentFarmId)).map(f=>(
                            <option key={f.id} value={f.id}>{f.name}</option>
                          ))}
                        </Select>
                        <div style={{position:"relative"}}>
                          <Input type="number" min="1" max="99" value={sf.pct}
                            onChange={e=>setForm(f=>({...f,sharedFarms:f.sharedFarms.map((x,j)=>j===i?{...x,pct:Math.min(99,Math.max(0,Number(e.target.value)))}:x)}))}
                            style={{paddingRight:24}}/>
                          <span style={{position:"absolute",right:10,top:"50%",transform:"translateY(-50%)",color:T.muted,fontSize:12}}>%</span>
                        </div>
                        <button onClick={()=>setForm(f=>({...f,sharedFarms:f.sharedFarms.filter((_,j)=>j!==i)}))}
                          style={{background:"none",border:`1px solid ${T.red}`,borderRadius:6,color:T.red,cursor:"pointer",width:36,height:36,fontSize:16}}>×</button>
                      </div>
                    );
                  })}
                  {(form.sharedFarms||[]).length>0&&(()=>{
                    const totalShared=(form.sharedFarms||[]).reduce((s,x)=>s+(x.pct||0),0);
                    const primaryPct=Math.max(0,100-totalShared);
                    const primaryFarm=(allFarms||[]).find(f=>f.id===(form.primaryFarmId||currentFarmId));
                    return(
                      <div style={{marginTop:8,padding:"8px 12px",borderRadius:8,background:`${T.accent}12`,border:`1px solid ${T.accent}33`,fontSize:12}}>
                        <div style={{color:T.accent,fontWeight:700,marginBottom:4}}>📊 Rateio do salário R$ {fmtMoney(form.salary||0)}:</div>
                        <div style={{color:T.text}}>🏠 {primaryFarm?.name||"Contrato"}: <strong>{primaryPct}%</strong> = R$ {fmtMoney((form.salary||0)*primaryPct/100)}</div>
                        {(form.sharedFarms||[]).map((sf,i)=>{
                          const f2=(allFarms||[]).find(f=>f.id===sf.farmId);
                          return <div key={i} style={{color:T.text}}>🌾 {f2?.name||sf.farmId}: <strong>{sf.pct}%</strong> = R$ {fmtMoney((form.salary||0)*(sf.pct||0)/100)}</div>;
                        })}
                        {totalShared>100&&<div style={{color:T.red,marginTop:4}}>⚠️ Percentuais somam {totalShared}% — máximo é 100%</div>}
                      </div>
                    );
                  })()}
                </div>
              </div>
            </Field>
          </>}

          {/* ── Advertências (admin only) ── */}
          {isAdmin&&<>
            <div style={{gridColumn:"1/-1",fontSize:11,fontWeight:700,color:T.yellow,textTransform:"uppercase",letterSpacing:.8,borderBottom:`1px solid ${T.border}`,paddingBottom:6,marginTop:8}}>
              ⚠️ Advertências
            </div>
            <Field label="" col="1/-1">
              <div style={{display:"flex",flexDirection:"column",gap:6}}>
                {(form.warnings||[]).map((w,i)=>(
                  <div key={i} style={{display:"flex",gap:8,alignItems:"center",background:T.card2,borderRadius:8,padding:"8px 12px"}}>
                    <Input value={w.date} type="date" onChange={e=>{const ww=[...(form.warnings||[])];ww[i]={...ww[i],date:e.target.value};setForm(f=>({...f,warnings:ww}));}} style={{width:140}}/>
                    <Input value={w.reason} onChange={e=>{const ww=[...(form.warnings||[])];ww[i]={...ww[i],reason:e.target.value};setForm(f=>({...f,warnings:ww}));}} placeholder="Motivo..." style={{flex:1}}/>
                    <button onClick={()=>setForm(f=>({...f,warnings:(f.warnings||[]).filter((_,j)=>j!==i)}))} style={{background:"none",border:`1px solid ${T.red}`,borderRadius:6,padding:"4px 8px",color:T.red,cursor:"pointer",fontSize:12}}>×</button>
                  </div>
                ))}
                <Btn size="sm" variant="ghost" onClick={()=>setForm(f=>({...f,warnings:[...(f.warnings||[]),{date:today(),reason:""}]}))}>+ Advertência</Btn>
              </div>
            </Field>
          </>}

          {/* ── Controle de visibilidade do Gestor (admin only) ── */}
          {isAdmin&&<>
            <div style={{gridColumn:"1/-1",fontSize:11,fontWeight:700,color:T.purple,textTransform:"uppercase",letterSpacing:.8,borderBottom:`1px solid ${T.border}`,paddingBottom:6,marginTop:8}}>
              👁️ O que o Gestor pode ver deste colaborador
            </div>
            <Field label="" col="1/-1">
              <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                {[
                  {key:"salary",  label:"💰 Salário"},
                  {key:"hired",   label:"📅 Admissão"},
                  {key:"notes",   label:"📝 Observações"},
                  {key:"warnings",label:"⚠️ Advertências"},
                ].map(f=>{
                  const checked=(form.gestorVisibleFields||[]).includes(f.key);
                  return(
                    <label key={f.key} style={{display:"flex",alignItems:"center",gap:6,cursor:"pointer",padding:"8px 12px",borderRadius:8,border:`1px solid ${checked?T.purple:T.border}`,background:checked?`${T.purple}18`:T.card2,fontSize:13,transition:"all .15s"}}>
                      <input type="checkbox" checked={checked} onChange={e=>setForm(frm=>({...frm,gestorVisibleFields:e.target.checked?[...(frm.gestorVisibleFields||[]),f.key]:(frm.gestorVisibleFields||[]).filter(k=>k!==f.key)}))} style={{accentColor:T.purple}}/>
                      {f.label}
                    </label>
                  );
                })}
              </div>
              <div style={{fontSize:11,color:T.muted,marginTop:6}}>O colaborador nunca vê salário, advertências ou observações internas.</div>
            </Field>
          </>}

          <div style={{gridColumn:"1/-1",display:"flex",gap:10,justifyContent:"flex-end",marginTop:4}}>
            {!form._new&&canDelete&&(
              ((form?.active===false) || String(form?.status||"").trim().toLowerCase()==="inativo" || !!(form?.deactivatedAt || form?.deactivated_at))
                ? <Btn variant="ghost" onClick={()=>setReactivateFlow({employee:form, reason:"", note:""})}>Ativar</Btn>
                : <Btn variant="danger" onClick={()=>setDeleteFlow({employee:form, reason:"", note:""})}>Desativar</Btn>
            )}
            <Btn variant="ghost" onClick={()=>{setForm(null);setEditingFlag(false);}}>Cancelar</Btn>
            {((form&&form._new&&canCreate)||(form&&!form._new&&canEdit))&&<Btn onClick={save}>Salvar</Btn>}
          </div>
        </div>}
      </div>
;

  const focusedEmployeeId = String((form && !form._new ? form.id : "") || deleteFlow?.employee?.id || reactivateFlow?.employee?.id || employeeHistory?.employee?.id || "");
  const isEmployeeInlineFocusMode = !!(form || deleteFlow || reactivateFlow || employeeHistory);

  return <div><Toast toast={toast}/>
    {summaryCards.length>0&&<div style={{display:"grid",gridTemplateColumns:"repeat(2,minmax(0,1fr))",gap:12,marginBottom:24}}>
      {summaryCards.map(card=>(
        <StatCard key={card.id} icon={card.icon} label={card.label} sub={card.sub} value={card.value} color={card.color} iconPosition="middleRight" iconWrapStyle={{width:50,height:50,minWidth:50,top:12,right:12}} iconStyle={card.iconStyle} contentPadRight={132}/>
      ))}
    </div>}
    <ModuleHeaderMenu
      T={T}
      title="Menu de RH"
      items={[
        { id:"emp", label:"Colaboradores", icon:"👥", active:tab==="emp", onClick:()=>{setTab("emp");setCheckEmp(null);} },
        { id:"chk", label:"EPIs & Equipamentos", icon:"🦺", active:tab==="chk", onClick:()=>setTab("chk") },
      ]}
    />

    {tab==="emp"&&<>
      <div style={{display:"flex",justifyContent:"flex-end",marginBottom:14}}>
        {canCreate&&<Btn onClick={()=>{ setEditingFlag(true); setForm(autoFillEmployeeAccess({...defForm, existingAccounts:accounts, reservedUsernames})); }}>+ Novo Colaborador(a)</Btn>}
      </div>
      <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:14}}>
        {["Ativos","Desativados","Todos"].map(opt=>(
          <button key={opt} onClick={()=>setEmployeeStatusFilter(opt)}
            style={{padding:"8px 14px",borderRadius:999,border:`1px solid ${employeeStatusFilter===opt?T.accent:T.border}`,background:employeeStatusFilter===opt?`${T.accent}18`:T.card,color:employeeStatusFilter===opt?T.accent:T.muted,fontWeight:700,cursor:"pointer"}}>
            {opt}
          </button>
        ))}
      </div>
      {form?._new && <div ref={hrInlineTopRef}>{renderEmployeeInlineEditor({marginTop:0, marginBottom:18})}</div>}

      {!form?._new && (isCompactViewport() ? (
        <div style={{display:"grid",gridTemplateColumns:"1fr",gap:12}}>
          {(isEmployeeInlineFocusMode
            ? (focusedEmployeeId ? filteredEmployeesView.filter(r=>String(r.id)===focusedEmployeeId) : [])
            : filteredEmployeesView
          ).map(r=>{
            const lvl=COLLAB_LEVELS.find(l=>l.id===(r.collabLevel||1));
            const isPrimary=(r.primaryFarmId||currentFarmId||"")===currentFarmId;
            const primaryFarm=(allFarms||[]).find(f=>f.id===(r.primaryFarmId||currentFarmId||""));
            const sharedHere=(r.sharedFarms||[]).find(sf=>sf.farmId===currentFarmId);
            const equips=((checklists.find(c=>c.empId===r.id)?.items)||[]).filter(i=>i.type==="Equipamento"&&i.withEmployee).length;
            const epis=((checklists.find(c=>c.empId===r.id)?.items)||[]).filter(i=>i.type==="EPI"&&i.withEmployee).length;
            const confs=((checklists.find(c=>c.empId===r.id)?.conferences)||[]).length;
            return (
              <React.Fragment key={r.id}>
                <div
                  ref={el=>{ if(el) hrInlineRefs.current.set(String(r.id), el); else hrInlineRefs.current.delete(String(r.id)); }}
                  style={{
                    background:T.card,
                    border:`1px solid ${T.border}`,
                    borderRadius:16,
                    padding:14,
                    cursor:"default",
                    display:"grid",
                    gap:10,
                    boxShadow:`0 6px 18px ${T.shadow || 'rgba(0,0,0,.06)'}`,
                  }}
                >
                  <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:12}}>
                    <div style={{minWidth:0,flex:1}}>
                      <div style={{display:"flex",flexDirection:"column",gap:4}}><div style={{fontSize:10,fontWeight:800,color:T.accent,letterSpacing:.6}}>{displayHrRecordNumber(r)}</div><div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                        <div style={{fontWeight:800,color:T.text,fontSize:16,lineHeight:1.2}}>{r.name}</div>
                        {lvl&&<span style={{fontSize:10,background:`${lvl.color}22`,color:lvl.color,borderRadius:999,padding:"2px 8px",fontWeight:700}}>{lvl.icon} {lvl.label}</span>}
                      </div>
                      </div>{r.username&&<div style={{fontSize:11,color:T.accent,marginTop:4}}>@{r.username}</div>}
                      <div style={{display:"flex",gap:6,flexWrap:"wrap",marginTop:8}}>
                        <Badge color={r.status==="Ativo"?T.green:r.status==="Férias"?T.yellow:T.red}>{r.status}</Badge>
                        <Badge color={T.muted}>{r.role}</Badge>
                        <CCBadge cc={r.costCenter}/>
                      </div>
                    </div>
                    <div style={{display:"flex",alignItems:"flex-start",gap:10,flexShrink:0}}>
                      {canEdit && <Btn size="sm" onClick={()=>openEmployeeEditor(r)} style={{alignSelf:"center"}}>Editar</Btn>}
                      <div style={{width:52,height:52,borderRadius:16,background:`${T.purple}18`,color:T.purple,display:"flex",alignItems:"center",justifyContent:"center",fontSize:28,flexShrink:0}}>👤</div>
                    </div>
                  </div>
                  <div style={{display:"grid",gridTemplateColumns:"repeat(2,minmax(0,1fr))",gap:8}}>
                    <div style={{background:T.card2,border:`1px solid ${T.border}`,borderRadius:12,padding:"10px 12px"}}>
                      <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:.6}}>Telefone</div>
                      <div style={{fontSize:13,color:T.text,marginTop:4}}>{r.phone||"—"}</div>
                    </div>
                    <div style={{background:T.card2,border:`1px solid ${T.border}`,borderRadius:12,padding:"10px 12px"}}>
                      <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:.6}}>Admissão</div>
                      <div style={{fontSize:13,color:T.text,marginTop:4}}>{(isAdmin||gestorCanSee(r,"hired"))?(r.hired?fmtDate(r.hired):"—"):"🔒"}</div>
                    </div>
                  </div>
                  <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                    <Badge color={T.green}>🦺 {epis} EPIs</Badge>
                    <Badge color={T.accent}>📻 {equips} Equip.</Badge>
                    <Badge color={T.purple}>🔎 {confs} conf.</Badge>
                    {(isAdmin||gestorCanSee(r,"warnings")) && <Badge color={T.yellow}>⚠️ {(r.warnings||[]).length} adv.</Badge>}
                    {(isAdmin||gestorCanSee(r,"salary")) && <Badge color={T.yellow}>💰 R$ {fmtMoney(r.salary||0)}</Badge>}
                  </div>
                  {r.status==="Inativo" && (
                    <div>
                      <button onClick={(e)=>{ e.stopPropagation(); setEmployeeHistory(buildEmployeeHistory(r)); }} style={{background:"none",border:`1px solid ${T.accent}`,borderRadius:999,padding:"7px 12px",color:T.accent,fontWeight:700,cursor:"pointer"}}>Ver histórico de participação</button>
                    </div>
                  )}
                  {!isPrimary&&primaryFarm&&(
                    <div style={{fontSize:11}}>
                      <span style={{background:`${T.yellow}22`,color:T.yellow,borderRadius:999,padding:"3px 8px",border:`1px solid ${T.yellow}44`,fontWeight:700}}>
                        🔀 Compartilhado de <FarmInlineLabel farm={primaryFarm} size={12} textStyle={{fontWeight:700}} /> · {sharedHere?.pct||0}%
                      </span>
                    </div>
                  )}
                  {isPrimary&&(r.sharedFarms||[]).length>0&&(
                    <div style={{fontSize:11}}>
                      <span style={{background:`${T.accent}18`,color:T.accent,borderRadius:999,padding:"3px 8px",border:`1px solid ${T.accent}33`,fontWeight:700}}>
                        🌾 Atua em {(r.sharedFarms||[]).length} fazenda(s) adicional(ais)
                      </span>
                    </div>
                  )}

                </div>
                {form && !form._new && String(form.id)===String(r.id) && renderEmployeeInlineEditor({marginTop:12})}
              </React.Fragment>
            );
          })}
          {filteredEmployeesView.length===0&&<div style={{color:T.muted,padding:32,textAlign:"center"}}>Nenhum colaborador(a) encontrado para este filtro.</div>}
        </div>
      ) : (
        <div style={{overflowX:"auto"}}>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
            <thead><tr style={{borderBottom:`1px solid ${T.border}`}}>
              {tableCols.map(c=><th key={c.key} style={{padding:"10px 14px",textAlign:"left",color:T.muted,fontSize:11,fontWeight:700,textTransform:"uppercase",letterSpacing:.8}}>{c.label}</th>)}
              <th style={{padding:"10px 14px",textAlign:"right",color:T.muted,fontSize:11,fontWeight:700,textTransform:"uppercase",letterSpacing:.8}}>Ações</th>
            </tr></thead>
            <tbody>{(isEmployeeInlineFocusMode
              ? (focusedEmployeeId ? filteredEmployeesView.filter(r=>String(r.id)===focusedEmployeeId) : [])
              : filteredEmployeesView
            ).map((r,i)=>(
              <React.Fragment key={r.id||i}>
                <tr
                  ref={el=>{ if(el) hrInlineRefs.current.set(String(r.id), el); else hrInlineRefs.current.delete(String(r.id)); }}
                  style={{borderBottom:`1px solid ${T.border}22`,transition:"background .12s"}}
                  onMouseEnter={e=>e.currentTarget.style.background=T.card2}
                  onMouseLeave={e=>e.currentTarget.style.background="transparent"}
                >
                  {tableCols.map(c=><td key={c.key} style={{padding:"12px 14px",color:T.text}}>{c.render?c.render(r):r[c.key]}</td>)}
                  <td style={{padding:"12px 14px",textAlign:"right"}}>{canEdit && <Btn size="sm" onClick={()=>openEmployeeEditor(r)}>Editar</Btn>}</td>
                </tr>
                {form && !form._new && String(form.id)===String(r.id) && (
                  <tr><td colSpan={tableCols.length+1} style={{padding:"0 0 16px 0"}}>{renderEmployeeInlineEditor({marginTop:12})}</td></tr>
                )}
              </React.Fragment>
            ))}</tbody>
          </table>
          {filteredEmployeesView.length===0&&<div style={{color:T.muted,padding:32,textAlign:"center"}}>Nenhum colaborador(a) encontrado para este filtro.</div>}
        </div>
      ))}
    </>}
    {deleteFlow&&(
      <div style={{marginTop:16,background:T.card,border:`1px solid ${T.red}33`,borderRadius:18,padding:18}}>
        <div style={{display:"flex",justifyContent:"space-between",gap:10,alignItems:"center",marginBottom:12}}>
          <div>
            <div style={{fontSize:11,fontWeight:800,color:T.red,textTransform:"uppercase",letterSpacing:.8}}>Fluxo inline</div>
            <div style={{fontSize:18,fontWeight:800,color:T.text,marginTop:4}}>Desativar colaborador</div>
          </div>
          <Btn variant="ghost" onClick={()=>setDeleteFlow(null)}>Fechar</Btn>
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:14}}>
          <div style={{fontSize:13,color:T.muted}}>A desativação mantém o colaborador arquivado no RH para rastreamento e também desativa a conta de usuário vinculada, quando existir. O histórico do que ele já participou continua disponível.</div>
          <Field label="Motivo da desativação *">
            <Select value={deleteFlow.reason||""} onChange={e=>setDeleteFlow(f=>({...f,reason:e.target.value}))}>
              <option value="">Selecione...</option>
              {(SL.EMPLOYEE_DELETE_REASONS||[]).map(reason=><option key={reason} value={reason}>{reason}</option>)}
            </Select>
          </Field>
          <Field label="Observação (opcional)">
            <Textarea rows={3} value={deleteFlow.note||""} onChange={e=>setDeleteFlow(f=>({...f,note:e.target.value}))}/>
          </Field>
          <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
            <Btn variant="ghost" onClick={()=>setDeleteFlow(null)}>Cancelar</Btn>
            <Btn variant="danger" onClick={()=>deactivateEmployeeCascade(deleteFlow.employee, deleteFlow.reason, deleteFlow.note)}>Desativar</Btn>
          </div>
        </div>
      </div>
    )}
    {reactivateFlow&&(
      <div style={{marginTop:16,background:T.card,border:`1px solid ${T.green}33`,borderRadius:18,padding:18}}>
        <div style={{display:"flex",justifyContent:"space-between",gap:10,alignItems:"center",marginBottom:12}}>
          <div>
            <div style={{fontSize:11,fontWeight:800,color:T.green,textTransform:"uppercase",letterSpacing:.8}}>Fluxo inline</div>
            <div style={{fontSize:18,fontWeight:800,color:T.text,marginTop:4}}>Reativar colaborador</div>
          </div>
          <Btn variant="ghost" onClick={()=>setReactivateFlow(null)}>Fechar</Btn>
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:14}}>
          <div style={{fontSize:13,color:T.muted}}>A reativação torna o colaborador visível novamente no RH e reativa a conta de usuário vinculada, quando existir. O histórico anterior é preservado.</div>
          <Field label="Motivo da reativação *">
            <Select value={reactivateFlow.reason||""} onChange={e=>setReactivateFlow(f=>({...f,reason:e.target.value}))}>
              <option value="">Selecione...</option>
              {(SL.EMPLOYEE_REACTIVATE_REASONS||[]).map(reason=><option key={reason} value={reason}>{reason}</option>)}
            </Select>
          </Field>
          <Field label="Observação (opcional)">
            <Textarea rows={3} value={reactivateFlow.note||""} onChange={e=>setReactivateFlow(f=>({...f,note:e.target.value}))}/>
          </Field>
          <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
            <Btn variant="ghost" onClick={()=>setReactivateFlow(null)}>Cancelar</Btn>
            <Btn onClick={()=>reactivateEmployeeCascade(reactivateFlow.employee, reactivateFlow.reason, reactivateFlow.note)}>Ativar</Btn>
          </div>
        </div>
      </div>
    )}
    {employeeHistory&&(
      <div style={{marginTop:16,background:T.card,border:`1px solid ${T.border}`,borderRadius:18,padding:isCompactViewport()?16:20,boxShadow:`0 10px 24px ${T.shadow || "rgba(0,0,0,.08)"}`}}>
        <div style={{display:"flex",justifyContent:"space-between",gap:10,alignItems:isCompactViewport()?"flex-start":"center",flexWrap:"wrap",marginBottom:12}}>
          <div>
            <div style={{fontSize:11,fontWeight:800,color:T.accent,textTransform:"uppercase",letterSpacing:.8}}>Fluxo inline</div>
            <div style={{fontSize:18,fontWeight:800,color:T.text,marginTop:4}}>{`Histórico de participação — ${employeeHistory.employee?.name||"Colaborador"}`}</div>
          </div>
          <Btn variant="ghost" onClick={()=>setEmployeeHistory(null)}>Fechar</Btn>
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:16}}>
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
            <Badge color={T.green}>Serviços: {employeeHistory.services.length}</Badge>
            <Badge color={T.red}>Ocorrências: {employeeHistory.occurrences.length}</Badge>
            <Badge color={T.purple}>Vistorias: {employeeHistory.inspections.length}</Badge>
            <Badge color={T.accent}>Compras: {employeeHistory.orders.length}</Badge>
          </div>
          <div style={{fontSize:12,color:T.muted}}>Mostrando registros desta fazenda em que o colaborador aparece vinculado por IDs fortes e, para itens antigos, por nome/username.</div>
          <div style={{display:"flex",flexDirection:"column",gap:10,maxHeight:"60vh",overflow:"auto",paddingRight:4}}>
            {employeeHistory.timeline.length===0&&<div style={{color:T.muted,padding:18,textAlign:"center",border:`1px dashed ${T.border}`,borderRadius:12}}>Nenhum vínculo encontrado nesta fazenda.</div>}
            {employeeHistory.timeline.map((item,idx)=>(
              <div key={`${item.type}:${item.id||idx}`} style={{background:T.card2,border:`1px solid ${T.border}`,borderRadius:12,padding:14}}>
                <div style={{display:"flex",justifyContent:"space-between",gap:10,alignItems:"flex-start"}}>
                  <div>
                    <div style={{fontSize:12,fontWeight:800,color:T.accent,textTransform:"uppercase",letterSpacing:.6}}>{item.type}</div>
                    <div style={{fontSize:15,fontWeight:700,color:T.text,marginTop:4}}>{item.title}</div>
                    <div style={{fontSize:12,color:T.muted,marginTop:6}}>{item.date ? fmtDate(item.date) : "Sem data"}{item.costCenter ? ` · ${item.costCenter}` : ""}</div>
                  </div>
                  <Badge color={item.status==="Concluído"?T.green:item.status==="Inativo"?T.red:T.yellow}>{item.status||"—"}</Badge>
                </div>
              </div>
            ))}
          </div>
          <div style={{display:"flex",justifyContent:"flex-end"}}>
            <Btn variant="ghost" onClick={()=>setEmployeeHistory(null)}>Fechar</Btn>
          </div>
        </div>
      </div>
    )}

    {tab==="chk"&&<>
      {!checkEmp ? <>
        <div style={{marginBottom:16,color:T.muted,fontSize:13}}>Selecione um colaborador(a) para ver e editar seu checklist de EPIs e equipamentos.</div>
        {filteredEmployeesView.length===0&&<div style={{color:T.muted,padding:32,textAlign:"center"}}>Nenhum colaborador(a) encontrado para este filtro.</div>}
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))",gap:12}}>
          {filteredEmployeesView.map(emp=>{
            const cl = checklists.find(c=>c.empId===emp.id)||{items:[],conferences:[]};
            const epis  = cl.items.filter(i=>i.type==="EPI"&&i.withEmployee).length;
            const equips= cl.items.filter(i=>i.type==="Equipamento"&&i.withEmployee).length;
            const confs = cl.conferences.length;
            const lastConf = cl.conferences.length ? [...cl.conferences].sort((a,b)=>b.date.localeCompare(a.date))[0].date : null;
            return <div key={emp.id} onClick={()=>setCheckEmp(emp)}
              style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:12,padding:18,cursor:"pointer",transition:"all .2s"}}
              onMouseEnter={e=>{e.currentTarget.style.borderColor=T.accent;e.currentTarget.style.transform="translateY(-2px)";}}
              onMouseLeave={e=>{e.currentTarget.style.borderColor=T.border;e.currentTarget.style.transform="translateY(0)";}}>
              <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:12}}>
                <div style={{width:40,height:40,borderRadius:"50%",background:`${T.accent}22`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,fontWeight:700,color:T.accent,flexShrink:0}}>{emp.name.charAt(0).toUpperCase()}</div>
                <div>
                  <div style={{fontWeight:700,color:T.text,fontSize:14}}>{emp.name}</div>
                  <div style={{fontSize:11,color:T.muted}}>{emp.role} · <CCBadge cc={emp.costCenter}/></div>
                </div>
              </div>
              <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                <Badge color={T.green}>🦺 {epis} EPI{epis!==1?"s":""}</Badge>
                <Badge color={T.accent}>🔧 {equips} Equip.</Badge>
                <Badge color={T.purple}>📅 {confs} conf.</Badge>
              </div>
              {lastConf&&<div style={{fontSize:11,color:T.muted,marginTop:8}}>Última conferência: {fmtDate(lastConf)}</div>}
              <div style={{marginTop:10,fontSize:12,fontWeight:600,color:T.accent}}>Ver checklist →</div>
            </div>;
          })}
        </div>
      </> : <>
        <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:20}}>
          <button onClick={()=>setCheckEmp(null)} style={{background:"none",border:`1px solid ${T.border}`,borderRadius:8,padding:"7px 14px",cursor:"pointer",color:T.muted,fontSize:13}}>← Voltar</button>
          <div style={{width:38,height:38,borderRadius:"50%",background:`${T.accent}22`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,fontWeight:700,color:T.accent}}>{checkEmp.name.charAt(0).toUpperCase()}</div>
          <div>
            <div style={{fontFamily:"'Playfair Display',serif",fontSize:18,fontWeight:700,color:T.text}}>{checkEmp.name}</div>
            <div style={{fontSize:12,color:T.muted}}>{checkEmp.role} · <CCBadge cc={checkEmp.costCenter}/></div>
          </div>
        </div>
        <EmployeeChecklist empId={checkEmp.id} empName={checkEmp.name} checklists={checklists} setChecklists={setChecklists}/>
      </>}
    </>}
  </div>;
}

  return HRModule;
}

export default createHRModule;
